import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import styled from 'styled-components';
import HeroImage from './HeroImage';
import { HeroData } from '../models/HeroData';
import DraftState from '../../../models/DraftState';
import { useDraft, MAP_OPTIONS } from '../contexts/DraftContext';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import { useNavigate } from 'react-router-dom';
import UserTierService from '../../../models/UserTier';

// Color palette for different match pulls
const MATCH_COLORS = [
  { r: 76, g: 175, b: 80 },   // Green
  { r: 255, g: 152, b: 0 },   // Orange
  { r: 156, g: 39, b: 176 },  // Purple
  { r: 33, g: 150, b: 243 },  // Blue
  { r: 244, g: 67, b: 54 },   // Red
  { r: 0, g: 188, b: 212 },   // Cyan
  { r: 255, g: 235, b: 59 },  // Yellow
  { r: 233, g: 30, b: 99 },   // Pink
];

const getMatchColor = (colorIndex) => {
  const color = MATCH_COLORS[colorIndex % MATCH_COLORS.length];
  return color;
};

// Per-map border color palette — neon/electric tones matching ygdraftplan style
const MAP_BORDER_COLORS = {
  'General': { r: 160, g: 160, b: 160 }, // Silver-white
  'Broken Wall': { r: 255, g: 60, b: 60 }, // Neon Red
  'Dangerous Grass': { r: 57, g: 255, b: 20 }, // Electric Lime Green
  'Expanding River': { r: 0, g: 200, b: 255 }, // Neon Cyan-Blue
  'Flying Cloud': { r: 255, g: 220, b: 0 }, // Neon Gold-Yellow
};

const DraftBoardContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #0b0d14;
  background-image: linear-gradient(to bottom, #161926, #0b0d14);
  padding: 12px 8px;
  border-radius: 12px;
  width: 204px;
  min-width: 204px;
  max-width: 204px;
  flex-shrink: 0;
  margin: 0;
  position: relative;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  transition: all 0.2s ease;
  border: 3px solid transparent;

  /* Gradient highlight - green for normal, red for swapped */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 50%;
    background: ${props => props.$swapSides ? `linear-gradient(
      to right,
      rgba(255, 70, 70, 0.45) 0%,
      rgba(255, 90, 90, 0.28) 25%,
      rgba(255, 70, 70, 0.15) 50%,
      transparent 100%
    )` : `linear-gradient(
      to right,
      rgba(76, 175, 80, 0.2) 0%,
      rgba(139, 195, 74, 0.12) 25%,
      rgba(76, 175, 80, 0.06) 50%,
      transparent 100%
    )`};
    border-radius: 12px 0 0 12px;
    pointer-events: none;
    z-index: 2;
  }

  /* Breathing effect for match-pulled scenarios with unique colors */
  ${props => {
    if (props.$fromMatchPull && typeof props.$matchColorIndex === 'number') {
      const color = getMatchColor(props.$matchColorIndex);
      return `
        border: 3px solid rgba(${color.r}, ${color.g}, ${color.b}, 0.6);
        animation: breathingGlow-${props.$matchColorIndex % MATCH_COLORS.length} 3s ease-in-out infinite;
      `;
    }
    return '';
  }}

  /* Generate breathing keyframes for each MATCH color */
  ${MATCH_COLORS.map((color, index) => `
    @keyframes breathingGlow-${index} {
      0%, 100% {
        border-color: rgba(${color.r}, ${color.g}, ${color.b}, 0.6);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5),
                    0 0 15px rgba(${color.r}, ${color.g}, ${color.b}, 0.4);
      }
      50% {
        border-color: rgba(${color.r}, ${color.g}, ${color.b}, 1);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5),
                    0 0 25px rgba(${color.r}, ${color.g}, ${color.b}, 0.6);
      }
    }
  `).join('\n')}

  /* Grey out base when dragging */
  ${props => props.$isDragging && `
    opacity: 0.3;
    filter: grayscale(0.5);
  `}

  /* Add margin when this scenario is the drop target */
  ${props => props.$isOver && `
    margin-left: 40px;
  `}

  /* Ripple animation when dropped */
  ${props => props.$justDropped && `
    animation: dropRipple 0.6s ease-out;
  `}

  @keyframes dropRipple {
    0% {
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5),
                  0 0 0 0 rgba(33, 150, 243, 0.7);
    }
    50% {
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5),
                  0 0 0 10px rgba(33, 150, 243, 0.3);
    }
    100% {
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5),
                  0 0 0 20px rgba(33, 150, 243, 0);
    }
  }

  @media (max-width: 768px) {
    width: 196px;
    min-width: 196px;
    max-width: 196px;
    padding: 8px 6px;
  }

  @media (max-width: 480px) {
    width: 188px;
    min-width: 188px;
    max-width: 188px;
    padding: 6px 4px;
  }

  @media (max-width: 600px) and (orientation: portrait) {
    zoom: 0.72;
  }
`;

const DragHandle = styled.div.attrs(props => ({
  'data-drag-handle': true,
  'draggable': true
}))`
  width: 100%;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  margin-bottom: 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.05);
  transition: background 0.2s;
  user-select: none;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:active {
    cursor: grabbing;
  }

  &::before {
    content: '⋮⋮⋮';
    color: rgba(255, 255, 255, 0.3);
    font-size: 16px;
    letter-spacing: 2px;
    pointer-events: none;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #333;
`;

const TeamsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 10px;
  align-items: center;
`;

const TeamHeader = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${props => props.$team === 'Radiant' ? '#92ff92' : '#ff6b6b'};
  text-align: center;
  width: 50%;
  text-shadow: 0 0 8px ${props => props.$team === 'Radiant' ? 'rgba(146, 255, 146, 0.5)' : 'rgba(255, 107, 107, 0.5)'};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
`;

const ClearButton = styled.button`
  background-color: #d32f2f;
  background-image: linear-gradient(to bottom, #d32f2f, #b71c1c);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);

  &:hover {
    background-image: linear-gradient(to bottom, #e33c3c, #c82121);
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }
`;

const PicksContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  position: relative;
  gap: 10px;
  background: linear-gradient(to bottom, rgba(20, 22, 34, 0.8), rgba(14, 16, 26, 0.8));
  padding: 10px 4px;
  border-radius: 8px;
`;

const TeamColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

// MLBB position label mapping - converts internal position numbers to display labels
const getDisplayPosition = (position) => {
  const positionMapping = {
    4: 3,
    3: 4,
    7: 5,
    5: 6,
    14: 7,
    13: 8,
    16: 9,
    15: 10,
    18: 11,
    17: 12,
    26: 13,
    27: 14,
    21: 15,
    22: 16,
    24: 17,
    23: 18,
    20: 19,
    25: 20
  };

  return positionMapping[position] || position;
};

// MLBB uses square boxes (same width and height)
const getBoxSize = (position) => {
  if (DraftState.getLargeBoxSequences().includes(position.toString())) {
    return '80px';
  }
  return '60px';
};

// MLBB uses rectangular boxes with rounded corners (matching mlbbbuff2 match history style)
const PickBox = styled.div.attrs(props => ({
  'data-position': props.$position,
  'data-team': props.$team
}))`
  border: 1px solid #444;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${props => props.$sizeOverride || getBoxSize(props.$position)};
  width: ${props => props.$widthOverride || props.$sizeOverride || getBoxSize(props.$position)};
  position: relative;
  z-index: 3;
  cursor: ${props => props.$isSimulatorView ? 'default' : 'pointer'};
  transition: all 0.2s ease;
  background-color: #111215;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  overflow: visible; /* Allow connector handle to overflow outside box edge */
  padding: 0;

  &:hover {
    ${props => !props.$isSimulatorView && `
      border-color: ${props.$team === 'Radiant' ? '#92ff92' : '#ff6b6b'};
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
    `}
  }

  &.focused {
    border-color: ${props => props.$team === 'Radiant' ? '#92ff92' : '#ff6b6b'};
    border-width: 2px;
    box-shadow: 0 0 10px ${props => props.$team === 'Radiant' ? 'rgba(146, 255, 146, 0.5)' : 'rgba(255, 107, 107, 0.5)'};
  }

  &.multi-selected {
    border-color: #2196F3;
    border-width: 3px;
    box-shadow: 0 0 15px rgba(33, 150, 243, 0.8);
    background-color: rgba(33, 150, 243, 0.1);
  }

  &.filled {
    background-color: ${props => props.$team === 'Radiant' ? 'rgba(146, 255, 146, 0.1)' : 'rgba(255, 107, 107, 0.1)'};
  }
`;

// MLBB PositionLabel uses display position coloring (red for bans, green for picks)
const PositionLabel = styled.div`
  position: absolute;
  top: 8px;
  left: 6px;
  font-size: 12px;
  font-weight: bold;
  color: ${props => {
    const displayPos = getDisplayPosition(props.$position);
    if ([1, 2, 3, 4, 5, 6, 13, 14, 15, 16].includes(displayPos)) {
      return '#ff0000'; // Red for ban positions
    } else if ([7, 8, 9, 10, 11, 12, 17, 18, 19, 20].includes(displayPos)) {
      return '#00ff00'; // Green for pick positions
    }
    return 'rgba(255, 255, 255, 0.8)'; // Default white
  }};
  background-color: rgba(0, 0, 0, 0.5);
  padding: 1px 3px;
  border-radius: 2px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  z-index: 2;

  ${({ $compact }) => $compact && `
    font-size: 6px;
    background-color: rgba(0, 0, 0, 0.25);
  `}
`;

const HeroContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0;
  overflow: hidden;
  border-radius: inherit; /* Clip hero image to PickBox rounded corners */

  > div {
    width: 100%;
    height: 100%;
  }
`;

const BanIndicator = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: rgba(211, 47, 47, 0.8);
  color: white;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
`;

const BanCross = styled.div`
  position: absolute;
  bottom: 2px;
  left: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff0000;
  font-weight: bold;
  font-size: 18px;
  pointer-events: none;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.9);
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 50%;
    z-index: -1;
  }
`;

const HeroSelectPopup = styled.div`
  position: fixed;
  z-index: 1000;
  width: 90vw;
  max-width: 600px;
  max-height: 80vh;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);

  @media (max-height: 600px) and (orientation: landscape) {
    width: 60vw;
    max-height: 90vh;
    top: 50%;
  }
  background-color: rgba(14, 16, 22, 0.97);
  border: 1px solid #444;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  animation: popupFadeIn 0.25s cubic-bezier(0.23, 1, 0.32, 1);

  @keyframes popupFadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`;

const SearchInput = styled.input`
  padding: 14px 16px;
  background-color: rgba(10, 12, 16, 0.7);
  color: white;
  border: none;
  border-bottom: 1px solid #555;
  margin-bottom: 0;
  font-size: 15px;
  width: 100%;
  box-sizing: border-box;
  position: sticky;
  top: 0;
  z-index: 3;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    background-color: rgba(15, 18, 24, 0.9);
    border-bottom-color: #4CAF50;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  font-size: 16px;
  pointer-events: none;
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2px;
  padding: 8px;

  @media (max-height: 600px) and (orientation: landscape) {
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    padding: 4px;
  }
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
  justify-content: center;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #555;
    border-radius: 4px;

    &:hover {
      background-color: #666;
    }
  }

  &::-webkit-scrollbar-track {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }
`;

const HeroOption = styled.div`
  cursor: pointer;
  padding: 1px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:active {
    transform: translateY(1px);
  }

  .hero-name {
    font-size: 9px;
    margin-top: 2px;
    text-align: center;
    color: #ccc;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    font-weight: 400;
  }
`;

const PopupControls = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px;
  border-top: 1px solid #444;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(5px);
`;

const RemoveButton = styled.button`
  background-color: #333;
  background-image: linear-gradient(to bottom, #444, #333);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 14px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);

  &:hover {
    background-image: linear-gradient(to bottom, #555, #444);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.5);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
    background-image: linear-gradient(to bottom, #333, #2a2a2a);
  }
`;

const BanBox = styled(PickBox)`
  img {
    filter: grayscale(70%) brightness(1.1);
  }
`;

// Edge handle for initiating a counter-relation connector drag
const ConnectorHandle = styled.div`
  position: absolute;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  border: 1.5px solid #00b4d8;
  box-shadow: 0 1px 3px rgba(0,0,0,0.5);
  cursor: crosshair;
  z-index: 10;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s, transform 0.12s;
  user-select: none;

  ${PickBox}:hover &, &.dragging {
    opacity: 1;
    pointer-events: auto;
  }

  &:hover {
    transform: scale(1.35);
    background: #00e0ff;
  }

  &.top    { top: -6px;    left: 50%; transform: translateX(-50%); }
  &.bottom { bottom: -6px; left: 50%; transform: translateX(-50%); }
  &.left   { left: -7px;   top: 50%;  transform: translateY(-50%); }
  &.right  { right: -7px;  top: 50%;  transform: translateY(-50%); }

  ${PickBox}:hover &.top,    &.top.dragging    { transform: translateX(-50%) scale(1); }
  ${PickBox}:hover &.bottom, &.bottom.dragging { transform: translateX(-50%) scale(1); }
  ${PickBox}:hover &.left,   &.left.dragging   { transform: translateY(-50%) scale(1); }
  ${PickBox}:hover &.right,  &.right.dragging  { transform: translateY(-50%) scale(1); }

  &.top:hover    { transform: translateX(-50%) scale(1.35); }
  &.bottom:hover { transform: translateX(-50%) scale(1.35); }
  &.left:hover   { transform: translateY(-50%) scale(1.35); }
  &.right:hover  { transform: translateY(-50%) scale(1.35); }

  @media (max-height: 600px) and (orientation: landscape) {
    display: none;
  }
`;

const BanCrossOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 5;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 8px;
  &::before, &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 10%;
    right: 10%;
    height: 3px;
    background: #ff3b30;
    box-shadow: 0 0 6px rgba(255, 59, 48, 0.9);
  }
  &::before { transform: translateY(-50%) rotate(20deg); }
  &::after  { transform: translateY(-50%) rotate(-20deg); }
`;

const ForeheadRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
  margin-bottom: 12px;
  width: 100%;
  flex-wrap: wrap;
  position: relative;
`;

// SVG overlay covering PicksContainer; draws counter-relation arrows.
const ConnectorSvg = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 5;
  overflow: visible;

  .connector-path {
    fill: none;
    stroke: #00ff88;
    stroke-width: 4;
    stroke-dasharray: 8 6;
    stroke-linecap: round;
    pointer-events: stroke;
    cursor: pointer;
    animation: connectorBreathing 3s ease-in-out infinite;
    transition: stroke-width 0.15s;
  }
  .connector-path:hover {
    stroke-width: 5.5;
  }
  .connector-path.drag-preview {
    stroke: #00b4d8;
    stroke-dasharray: 5 4;
    pointer-events: none;
    animation: none;
    filter: drop-shadow(0 0 4px rgba(0,180,216,0.7));
  }

  @keyframes connectorBreathing {
    0%, 100% {
      stroke: rgba(0, 255, 136, 0.85);
      filter: drop-shadow(0 0 3px rgba(0, 255, 136, 0.6));
    }
    50% {
      stroke: rgba(120, 255, 180, 1);
      filter: drop-shadow(0 0 10px rgba(0, 255, 136, 1)) drop-shadow(0 0 18px rgba(0, 255, 136, 0.5));
    }
  }
`;

const LegendContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  padding: 8px 12px;
  background-color: rgba(0, 0, 0, 0.4);
  border-radius: 6px;
  border: 1px solid #333;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #ddd;
`;

const LegendBanIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.3);
  color: #ff6b6b;
  font-weight: bold;
  font-size: 14px;
`;

const pillButtonBase = `
  color: white;
  border: none;
  border-radius: 999px;
  padding: 0 16px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  font-size: 14px;
  white-space: nowrap;

  @media (max-width: 600px) {
    height: 28px;
    font-size: 11px;
    padding: 0 10px;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }

  svg {
    margin-right: 6px;
  }
`;

const AddScenarioButton = styled.button`
  background-color: #2196F3;
  background-image: linear-gradient(to bottom, #2196F3, #1976D2);
  ${pillButtonBase}

  &:hover {
    background-image: linear-gradient(to bottom, #42A5F5, #1E88E5);
  }
`;

const NewDraftButton = styled.button`
  background-color: #1976D2;
  background-image: linear-gradient(to bottom, #1976D2, #1565C0);
  ${pillButtonBase}

  &:hover {
    background-image: linear-gradient(to bottom, #1E88E5, #1976D2);
  }
`;

const ClearMapsButton = styled.button`
  background-color: #E53935;
  background-image: linear-gradient(to bottom, #E53935, #B71C1C);
  ${pillButtonBase}

  &:hover {
    background-image: linear-gradient(to bottom, #EF5350, #C62828);
  }
`;

// Create a new ScenarioContainer for horizontal scrolling
const ScenarioContainer = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 8px;
  padding: 10px 20px;
  width: 100%;
  min-width: 100vw;
  scrollbar-width: thin;
  scrollbar-color: #444 #222;
  justify-content: safe center;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #222;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #444;
    border-radius: 4px;

    &:hover {
      background-color: #555;
    }
  }
`;

// Add a DeleteScenarioButton component
const DeleteScenarioButton = styled.button`
  background-color: transparent;
  color: #ff4d4d;
  border: 1px solid #ff4d4d;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 11px;
  font-weight: bold;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  min-width: 60px;
  justify-content: center;

  &:hover {
    background-color: rgba(255, 77, 77, 0.1);
  }

  svg {
    margin-right: 4px;
  }
`;


// Record Viewer button
const RecordViewerButton = styled.button`
  background-color: #0288d1;
  background-image: linear-gradient(to bottom, #0288d1, #01579b);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  height: 36px;
  font-size: 14px;

  &:hover {
    background-image: linear-gradient(to bottom, #039be5, #0277bd);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(2, 136, 209, 0.4);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
`;

// Add a DuplicateScenarioButton component
const DuplicateScenarioButton = styled.button`
  background-color: transparent;
  color: #61dafb;
  border: 1px solid #61dafb;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 11px;
  font-weight: bold;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  min-width: 70px;
  justify-content: center;

  &:hover {
    background-color: rgba(97, 218, 251, 0.1);
  }

  svg {
    margin-right: 4px;
  }
`;

// Add a CopyScenarioButton component
const CopyScenarioButton = styled.button`
  background-color: transparent;
  color: #4caf50;
  border: 1px solid #4caf50;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 11px;
  font-weight: bold;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  min-width: 70px;
  justify-content: center;

  &:hover {
    background-color: rgba(76, 175, 80, 0.1);
  }

  svg {
    margin-right: 4px;
  }
`;

const ScenarioTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
  color: #eee;
`;

const ScenarioInput = styled.input`
  background-color: rgba(0, 0, 0, 0.3);
  border: 1px solid #444;
  border-radius: 4px;
  color: white;
  padding: 8px 10px;
  width: 100%;
  margin-bottom: 12px;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #61dafb;
    box-shadow: 0 0 0 2px rgba(97, 218, 251, 0.2);
  }

  &::placeholder {
    color: #888;
  }
`;

const TopButtonRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-bottom: 16px;
  flex-wrap: wrap;
  position: sticky;
  top: 0;
  background: #121212;
  z-index: 100;
  padding: 12px 0;

  @media (max-width: 768px) {
    gap: 4px;
    margin-bottom: 12px;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    gap: 3px;
    padding: 4px 0;
    margin-bottom: 4px;

    button {
      height: 22px !important;
      font-size: 9px !important;
      padding: 0 6px !important;
      min-width: unset !important;
      border-radius: 999px !important;
    }
  }
`;

const ScenarioBoardsRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: safe center;
  align-items: stretch;
  gap: 6px;
  width: 100%;
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0;
  box-sizing: border-box;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }

  @media (max-width: 768px) {
    gap: 4px;
    padding: 4px 2px;
  }
`;

const InfoInputRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  width: 100%;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    display: none;
  }
`;

const InfoInput = styled.input`
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid #444;
  background: #181a20;
  color: #fff;
  font-size: 14px;
  min-width: 120px;
  max-width: 180px;
  outline: none;
  transition: border 0.2s;
  box-sizing: border-box;

  &:focus {
    border-color: #61dafb;
  }
`;

const InfoDateInput = styled(InfoInput).attrs({ type: 'date' })`
  min-width: 120px;
`;

const ToggleSwitchContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 12px;
`;

const DesktopOnlyToggleContainer = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 1024px), (max-height: 600px) and (orientation: landscape) {
    display: none;
  }
`;

const ToggleSwitch = styled.div`
  display: flex;
  border: 2px solid #1de9b6;
  border-radius: 999px;
  overflow: hidden;
  background: #181a20;
  min-width: 120px;
  height: 36px;

  @media (max-width: 600px) {
    height: 28px;
    min-width: unset;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    height: 22px;
    min-width: unset;
  }
`;

const ToggleOption = styled.button`
  background: ${props => props.$active ? '#1de9b6' : 'transparent'};
  color: ${props => props.$active ? '#000' : '#aaa'};
  border: none;
  padding: 0 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  transition: all 0.2s ease;
  flex: 1;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  @media (max-width: 600px) {
    font-size: 11px;
    padding: 0 10px;
  }

  &:hover {
    background: ${props => props.$active ? '#26efc2' : 'rgba(29, 233, 182, 0.1)'};
    color: ${props => props.$active ? '#000' : '#1de9b6'};
  }

  &:first-child {
    border-top-left-radius: 999px;
    border-bottom-left-radius: 999px;
  }

  &:last-child {
    border-top-right-radius: 999px;
    border-bottom-right-radius: 999px;
  }
`;


// Add drag-and-drop overlay styles
const DragOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(33, 150, 243, 0.08);
  backdrop-filter: blur(2px);
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 9999;
  border: 2px dashed rgba(33, 150, 243, 0.4);
`;

const DragOverlayContent = styled.div`
  background-color: rgba(0, 0, 0, 0.7);
  padding: 24px 32px;
  border-radius: 8px;
  text-align: center;
  color: #2196f3;
  font-size: 18px;
  font-weight: 500;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
`;

const DragOverlayIcon = styled.div`
  font-size: 40px;
  margin-bottom: 8px;
  opacity: 0.8;
`;

const MainContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 8px 0;
  box-sizing: border-box;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  @media (max-width: 768px) {
    padding: 4px;
  }

  @media (max-height: 600px) and (orientation: landscape) {
    padding: 0;
    align-items: stretch;
  }
`;

// Selection box for multi-select
const SelectionBox = styled.div`
  position: fixed;
  border: 2px solid #2196F3;
  background-color: rgba(33, 150, 243, 0.1);
  pointer-events: none;
  z-index: 9998;
`;

// Canvas ripple overlay for drop animation
const CanvasRipple = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 9997;
  opacity: ${props => props.$show ? 1 : 0};
  transition: opacity 0.3s ease;

  &::before {
    content: '';
    position: absolute;
    top: ${props => props.$y}px;
    left: ${props => props.$x}px;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(33, 150, 243, 0.3) 0%, rgba(33, 150, 243, 0.1) 40%, transparent 70%);
    transform: translate(-50%, -50%);
    animation: ${props => props.$show ? 'canvasRipple 1.2s ease-out' : 'none'};
  }

  @keyframes canvasRipple {
    0% {
      width: 0;
      height: 0;
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
    100% {
      width: 200vw;
      height: 200vw;
      opacity: 0;
    }
  }
`;

// Scenario selection overlay for "Copy From" functionality
const ScenarioSelectionOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.92);
  z-index: 999;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ScenarioSelectionHeader = styled.div`
  flex: 0 0 auto;
  text-align: center;
  color: #fff;
  padding: 24px 60px 16px;
`;

const ScenarioSelectionTitle = styled.h2`
  margin: 0 0 10px 0;
  font-size: 24px;
  color: #4caf50;
`;

const ScenarioSelectionSubtitle = styled.p`
  margin: 0 0 20px 0;
  font-size: 16px;
  color: #aaa;
`;

const ScenarioSelectionCloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  color: #aaa;
  font-size: 24px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  padding: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ScenarioSelectionGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
  max-width: 1400px;
  margin: 0 auto;
`;


// Map section wrapper - wraps each map's header + boards row
const AllMapsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: safe center;
  gap: 4px;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0 0 8px 0;

  &::-webkit-scrollbar {
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 152, 0, 0.4);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 152, 0, 0.7);
  }

  @media (max-height: 600px) and (orientation: landscape) {
    display: none;
  }
`;

const DesktopDrafterContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: 100%;
  flex: 1;
  overflow-x: scroll;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }

  @media (max-height: 600px) and (orientation: landscape) {
    display: none;
  }
`;

const DesktopDrafterCard = styled.div`
  width: 50vw;
  min-width: 50vw;
  flex-shrink: 0;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  box-sizing: border-box;
  background: linear-gradient(to bottom, #161926, #0b0d14);
  border-radius: 0;
  border: 2px solid ${props => props.$borderColor || 'rgba(160,160,160,0.3)'};
  gap: 8px;
  overflow-y: auto;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${props => props.$swapped
      ? 'linear-gradient(to right, rgba(255,70,70,0.28) 0%, rgba(255,70,70,0.10) 50%, transparent 100%)'
      : 'linear-gradient(to right, rgba(76,175,80,0.22) 0%, rgba(76,175,80,0.10) 50%, transparent 100%)'};
    pointer-events: none;
    z-index: 0;
  }

  ${BanCross} {
    display: none;
  }

  ${PositionLabel} {
    font-size: 11px;
  }
`;

// ── Mobile landscape carousel ────────────────────────────────────────────────
const MobileCarouselOuter = styled.div`
  display: none;
  width: 100%;
  flex: 1;

  @media (max-height: 600px) and (orientation: landscape) {
    display: ${props => props.$active ? 'flex' : 'none'};
    flex-direction: column;
  }

  /* Mobile-only overrides for pick/ban boxes */
  ${PositionLabel} {
    top: auto;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.25);
    padding: 1px 4px;
    border-radius: 0;
    border-top-right-radius: 4px;
    font-size: 7px;
  }

  ${BanCross} {
    display: none;
  }
`;

const MobileCarouselScroll = styled.div`
  display: flex;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  width: 100%;
  flex: 1;
  align-items: stretch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

// MobilePage: 100vw snap page containing a 2×2 grid of boards (column-major)
const MobilePage = styled.div`
  width: 100vw;
  min-width: 100vw;
  flex-shrink: 0;
  scroll-snap-align: start;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  grid-auto-flow: column;
  box-sizing: border-box;
`;

const MobileBoardCard = styled.div`
  /* $solo = General-style full-width card; default = inside a MobilePage grid cell */
  width: ${props => props.$solo ? '50vw' : 'auto'};
  min-width: ${props => props.$solo ? '50vw' : 'unset'};
  flex-shrink: ${props => props.$solo ? '0' : '1'};
  align-self: ${props => props.$solo ? 'flex-start' : 'auto'};
  scroll-snap-align: ${props => props.$solo ? 'start' : 'none'};
  display: flex;
  flex-direction: column;
  padding: 4px 6px;
  box-sizing: border-box;
  background: linear-gradient(to bottom, #161926, #0b0d14);
  border-radius: 0;
  border: 2px solid ${props => props.$borderColor || 'rgba(160,160,160,0.3)'};
  overflow: hidden;
  gap: 3px;
`;

const MobileBoardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

// Mobile draft grid: two side sections (Us | Enemy), each with bans row then picks row
const MobileDraftGrid = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  flex: 1;
  align-items: flex-start;
`;

const MobileSideSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const MobileSideLabel = styled.div`
  font-size: 11px;
  font-weight: bold;
  text-align: left;
  color: ${props => props.$team === 'Radiant' ? '#92ff92' : '#ff6b6b'};
  text-shadow: 0 0 6px ${props => props.$team === 'Radiant' ? 'rgba(146,255,146,0.5)' : 'rgba(255,107,107,0.5)'};
  margin-bottom: 2px;
`;

const MobileHeroRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 3px;
  flex-wrap: nowrap;
`;

const MobileSideDivider = styled.div`
  width: 1px;
  background: rgba(255, 255, 255, 0.12);
  align-self: stretch;
  flex-shrink: 0;
  margin: 0 2px;
`;

// ── Mobile landscape view toggle bar ─────────────────────────────────────────
const MobileLandscapeToggleBar = styled.div`
  display: none;
  @media (max-height: 600px) and (orientation: landscape) {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    padding: 3px 8px;
    background: rgba(0, 0, 0, 0.4);
  }
`;

const MobileLandscapeToggleSwitch = styled.div`
  display: flex;
  border: 2px solid #2196F3;
  border-radius: 999px;
  overflow: hidden;
  background: #181a20;
  height: 22px;
`;

const MobileLandscapeToggleOption = styled.button`
  background: ${props => props.$active ? '#2196F3' : 'transparent'};
  color: ${props => props.$active ? '#fff' : '#aaa'};
  border: none;
  padding: 0 12px;
  cursor: pointer;
  font-size: 10px;
  font-weight: ${props => props.$active ? 'bold' : 'normal'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  &:hover {
    background: ${props => props.$active ? '#42A5F5' : 'rgba(33,150,243,0.15)'};
    color: ${props => props.$active ? '#fff' : '#90CAF9'};
  }

  &:first-child { border-radius: 999px 0 0 999px; }
  &:last-child  { border-radius: 0 999px 999px 0; }
`;

// ── Drafter POV carousel styled components ────────────────────────────────────
const MobileDrafterOuter = styled.div`
  display: none;
  width: 100%;
  flex: 1;

  @media (max-height: 600px) and (orientation: landscape) {
    display: ${props => props.$active ? 'flex' : 'none'};
    flex-direction: column;
  }

  ${PositionLabel} {
    top: auto;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.25);
    padding: 1px 4px;
    border-radius: 0;
    border-top-right-radius: 4px;
    font-size: 7px;
  }

  ${BanCross} {
    display: none;
  }
`;

const MobileDrafterCard = styled.div`
  width: 50vw;
  min-width: 50vw;
  flex-shrink: 0;
  scroll-snap-align: start;
  display: flex;
  flex-direction: column;
  padding: 4px 8px;
  box-sizing: border-box;
  background: linear-gradient(to bottom, #161926, #0b0d14);
  border-radius: 0;
  border: 2px solid ${props => props.$borderColor || 'rgba(160,160,160,0.3)'};
  overflow: hidden;
  gap: 3px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${props => props.$swapped
      ? 'linear-gradient(to right, rgba(255,70,70,0.28) 0%, rgba(255,70,70,0.10) 50%, transparent 100%)'
      : 'linear-gradient(to right, rgba(76,175,80,0.22) 0%, rgba(76,175,80,0.10) 50%, transparent 100%)'};
    pointer-events: none;
    z-index: 0;
  }
`;

const MobileBansSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const MobileBansGroup = styled.div`
  display: flex;
  flex-direction: row;
  gap: 3px;
  align-items: center;
`;

const MobileTeamsLabel = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  flex-shrink: 0;
`;

const MobilePicksSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
`;

const MobilePickColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
`;

// Map section wrapper — ONE shared rectangle per map slot, with neon breathing border
const MapSectionWrapper = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  padding: 0;
  border: 2px solid transparent;

  ${props => {
    if (props.$mapColor) {
      const c = props.$mapColor;
      return `
        border-color: rgba(${c.r}, ${c.g}, ${c.b}, 0.55);
        background: rgba(${c.r}, ${c.g}, ${c.b}, 0.04);
        animation: mwBreath-${c.r}-${c.g}-${c.b} 6s ease-in-out infinite;
      `;
    }
    return '';
  }}

  /* Breathing keyframes for each map color — subtle 6s cycle, lower glow */
  ${Object.values({
    'General': { r: 160, g: 160, b: 160 },
    'Broken Wall': { r: 255, g: 60, b: 60 },
    'Dangerous Grass': { r: 57, g: 255, b: 20 },
    'Expanding River': { r: 0, g: 200, b: 255 },
    'Flying Cloud': { r: 255, g: 220, b: 0 },
  }).map(c => `
    @keyframes mwBreath-${c.r}-${c.g}-${c.b} {
      0%, 100% {
        border-color: rgba(${c.r}, ${c.g}, ${c.b}, 0.3);
        box-shadow: 0 0 4px rgba(${c.r}, ${c.g}, ${c.b}, 0.15);
      }
      50% {
        border-color: rgba(${c.r}, ${c.g}, ${c.b}, 0.7);
        box-shadow: 0 0 12px rgba(${c.r}, ${c.g}, ${c.b}, 0.4);
      }
    }
  `).join('\n')}
`;

// Map name pill label shown inside each DraftBoardContainer at the top
const MapDropdown = styled.select`
  display: block;
  margin: 0 auto 6px;
  background: rgba(255, 152, 0, 0.15);
  color: #ff9800;
  border: 1px solid rgba(255, 152, 0, 0.5);
  border-radius: 999px;
  padding: 3px 24px 3px 12px;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='%23ff9800' d='M5 7L1 3h8z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-color: rgba(255, 152, 0, 0.15);
  transition: border-color 0.2s, background-color 0.2s;

  &:hover {
    border-color: #ff9800;
    background-color: rgba(255, 152, 0, 0.25);
  }

  option {
    background: #1a1a2e;
    color: #fff;
    text-transform: none;
    font-weight: normal;
    letter-spacing: 0;
  }
`;

/**
 * DraftBoard Component
 *
 * Displays the MLBB draft board with circular pick boxes for Radiant and Dire.
 * Features from ygdraftplan integrated: drag-and-drop reordering, per-scenario
 * swap sides, PracticeMatchup, AllMatchupsView, copy-from/to pick mode,
 * ConfirmationDialog, UserTier feature gating, keyboard navigation, and more.
 */
const DraftBoard = ({
  isSharedView = false,
  sharedPermission = null,
  isSimulatorView = false,
  showButtons = null, // { addScenario, duplicate, delete, clearAll }
  isMarketingDemo = false,
  isDisabledDemo = false,
  maxScenariosDemo = null,
  isEmbedView = false // when true, parent (mlbbbuff2) hosts the board in an iframe overlay; minimize chrome
}) => {
  const navigate = useNavigate();
  const {
    scenariosFirstPick,
    scenariosSecondPick,
    mapDrafts,
    addScenario,
    duplicateScenario,
    deleteScenario,
    updateScenario,
    resetNonGeneralMapDrafts,
    reorderScenarios,
    moveScenarioToMap,
    initializeMap,
    saveDraft,
    newDraft,
    teamName, setTeamName,
    tournament, setTournament,
    stage, setStage,
    draftDate, setDraftDate,
    hasUnsavedChanges, setHasUnsavedChanges,
    currentDraftId,
    loadDraft,
    selectedMap,
    setSelectedMap,
    boardMaps,
    setBoardMaps,
    boardLabels,
    setBoardLabels,
    undo,
    redo,
    importDraftData,
    mergeDraftData,
    foreheadPicks,
    setForeheadPicks,
    foreheadConnectors,
    setForeheadConnectors,
  } = useDraft();

  // Toggle state: 'first' or 'second'
  const [pickMode, setPickMode] = useState('first');

  // Active map for interactions (box clicks, keyboard nav, hero selector)
  const [activeMap, setActiveMap] = useState('General');

  // Active slot index - tracks WHICH board slot is currently active (not just map name)
  // This prevents two boards showing the same map from sharing selection state
  const [activeSlotIndex, setActiveSlotIndex] = useState(0);

  // boardMaps and boardLabels now live in DraftContext so undo/redo covers them

  // Scenarios for the active map + pick mode (used for hero selector, keyboard nav, copy-from overlay)
  const scenarios = mapDrafts[activeMap]?.[pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick'] || [];

  // UI state
  const [selectedBox, setSelectedBox] = useState(-1);
  const [focusedBox, setFocusedBox] = useState({ scenarioIndex: 0, position: 1 });
  const [heroSearch, setHeroSearch] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [activeScenarioIndex, setActiveScenarioIndex] = useState(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const popupRef = useRef(null);
  const boxRefs = useRef({});
  const picksContainerRefs = useRef({});
  const selectionSlotRef = useRef(null); // slot index where the drag started
  const selectionMapRef = useRef(null);  // map name where the drag started
  const scenarioRefs = useRef({});

  // Forehead row state
  const [isForeheadMode, setIsForeheadMode] = useState(false);
  // Manually-crossed forehead slots (Cmd/Alt+click toggles)
  const [manualCrosses, setManualCrosses] = useState(() => new Set());
  const [foreheadSelection, setForeheadSelection] = useState(() => new Set());
  // Forehead row connector drag state (transient, not persisted)
  const [foreheadConnectorDrag, setForeheadConnectorDrag] = useState(null);
  const foreheadBoxRefs = useRef({});
  const foreheadRowRef = useRef(null);

  const clearForeheadCrosses = () => {
    setManualCrosses(new Set());
  };

  const toggleManualCross = (idx) => {
    setManualCrosses(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const handleForeheadBoxClick = (slotIndex, e) => {
    if (isDisabledDemo) return;
    if (e && (e.metaKey || e.ctrlKey || e.altKey)) {
      e.preventDefault();
      e.stopPropagation();
      toggleManualCross(slotIndex);
      return;
    }
    if (e && e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      setForeheadSelection(prev => {
        const next = new Set(prev);
        if (next.has(slotIndex)) next.delete(slotIndex); else next.add(slotIndex);
        return next;
      });
      return;
    }
    setForeheadSelection(new Set());
    setIsForeheadMode(true);
    setSelectedBox(slotIndex + 1);
    setShowPopup(true);
    if (selectedPositions.size > 0) setSelectedPositions(new Set());
  };

  const handleForeheadContextMenu = (e, slotIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setForeheadPicks(prev => { const next = [...prev]; next[slotIndex] = null; return next; });
    setForeheadConnectors(prev => prev.filter(c => c.from !== slotIndex && c.to !== slotIndex));
  };

  // Counter-relation connector drag state
  // { scenarioId, mapName, fromPosition, mouseX, mouseY } — mouse coords are in viewport space
  const [connectorDrag, setConnectorDrag] = useState(null);
  // Bumped to force connector SVG re-measure after layout changes (resize, scroll, swap)
  const [connectorTick, setConnectorTick] = useState(0);
  const scrollContainerRef = useRef(null);
  const searchInputRef = useRef(null);
  const [focusedHeroIndex, setFocusedHeroIndex] = useState(-1);
  const heroRefs = useRef({});

  // Mobile landscape view toggle: 'drafter' (default) | 'board'
  const [mobileLandscapeView, setMobileLandscapeView] = useState('drafter');
  // Desktop view toggle: 'normal' (default) | 'drafter'
  const [desktopView, setDesktopView] = useState('normal');

  // Multi-select state
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState({ x: 0, y: 0 });
  const [selectionEnd, setSelectionEnd] = useState({ x: 0, y: 0 });
  const [selectedPositions, setSelectedPositions] = useState(new Set());

  // Focus indicator visibility (hide after 10s inactivity, starts hidden)
  const [showFocusIndicator, setShowFocusIndicator] = useState(false);
  const inactivityTimeoutRef = useRef(null);

  // Clipboard state for copy-paste
  const [clipboard, setClipboard] = useState([]);

  // Drag-and-drop scenario reordering state
  const [draggedScenarioId, setDraggedScenarioId] = useState(null);
  const [draggedScenarioMap, setDraggedScenarioMap] = useState(null);
  const [dragOverScenarioId, setDragOverScenarioId] = useState(null);
  const [isScenarioDragging, setIsScenarioDragging] = useState(false);
  const [droppedScenarioId, setDroppedScenarioId] = useState(null);
  const [ripplePosition, setRipplePosition] = useState({ x: 0, y: 0, show: false });

  // Duplicate prevention toggle
  const [preventDuplicates, setPreventDuplicates] = useState(true);

  // Copy scenario state
  const [copySourceScenario, setCopySourceScenario] = useState(null);
  const [copySourcePickMode, setCopySourcePickMode] = useState(null);
  const [showScenarioSelection, setShowScenarioSelection] = useState(false);
  const [pendingCopyToScenarioId, setPendingCopyToScenarioId] = useState(null);
  const [pendingCopyMapName, setPendingCopyMapName] = useState(null);
  const [showBothOverlay, setShowBothOverlay] = useState(false);

  // Ref for draft content
  const draftContentRef = useRef(null);

  // userProfile stub for UserTierService - defaults to premium since no auth tier system in MLBB context
  const userProfile = { tier: 'premium' };

  // Hero filtering logic with abbreviation support (memoized)
  const allHeroes = React.useMemo(() => Object.values(HeroData), []);
  const filteredHeroes = React.useMemo(() => heroSearch
    ? allHeroes.filter(hero => {
      const searchTerm = heroSearch.toLowerCase().trim();
      const heroName = hero.name.toLowerCase();

      if (heroName.includes(searchTerm)) return true;

      const words = heroName.split(/[\s\-']+/).filter(word => word.length > 0);
      const abbreviation = words.map(word => word.charAt(0)).join('');

      if (abbreviation === searchTerm) return true;
      if (abbreviation.startsWith(searchTerm)) return true;
      if (words.some(word => word.startsWith(searchTerm))) return true;

      if (searchTerm.length === 1) {
        return abbreviation.startsWith(searchTerm) || words.some(word => word.startsWith(searchTerm));
      }

      return false;
    }).sort((a, b) => {
      const searchTerm = heroSearch.toLowerCase().trim();
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();

      const aStartsWith = aName.startsWith(searchTerm);
      const bStartsWith = bName.startsWith(searchTerm);
      if (aStartsWith && !bStartsWith) return -1;
      if (bStartsWith && !aStartsWith) return 1;

      const aWords = aName.split(/[\s\-']+/).filter(word => word.length > 0);
      const bWords = bName.split(/[\s\-']+/).filter(word => word.length > 0);
      const aAbbr = aWords.map(word => word.charAt(0)).join('');
      const bAbbr = bWords.map(word => word.charAt(0)).join('');

      const aAbbrMatch = aAbbr === searchTerm;
      const bAbbrMatch = bAbbr === searchTerm;
      if (aAbbrMatch && !bAbbrMatch) return -1;
      if (bAbbrMatch && !aAbbrMatch) return 1;

      return aName.localeCompare(bName);
    })
    : allHeroes, [heroSearch, allHeroes]);

  // Set active scenario to the first one on pickMode or map change
  useEffect(() => {
    if (scenarios.length > 0) {
      setActiveScenarioIndex(0);
    }
  }, [pickMode, selectedMap]);

  // Center the first scenario on pickMode or map change
  useEffect(() => {
    const centerFirstScenario = () => {
      if (scenarios.length > 0 && scrollContainerRef.current && scenarioRefs.current[scenarios[0].id]) {
        setTimeout(() => {
          const firstScenario = scenarioRefs.current[scenarios[0].id];
          const container = scrollContainerRef.current;

          if (firstScenario && container) {
            const scenarioRect = firstScenario.getBoundingClientRect();
            const scrollLeft = firstScenario.offsetLeft - (container.clientWidth / 2) + (scenarioRect.width / 2);
            container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
          }
        }, 100);
      }
    };

    centerFirstScenario();
  }, [pickMode, selectedMap]);

  // Center on initial mount
  useEffect(() => {
    const initialCenter = () => {
      if (scenarios.length > 0 && scrollContainerRef.current && scenarioRefs.current[scenarios[0].id]) {
        setTimeout(() => {
          const firstScenario = scenarioRefs.current[scenarios[0].id];
          const container = scrollContainerRef.current;

          if (firstScenario && container) {
            const scenarioRect = firstScenario.getBoundingClientRect();
            const scrollLeft = firstScenario.offsetLeft - (container.clientWidth / 2) + (scenarioRect.width / 2);
            container.scrollTo({ left: scrollLeft, behavior: 'auto' });
          }
        }, 300);
      }
    };

    initialCenter();
  }, []); // Mount only

  // Focus the focused box element so keyboard navigation works immediately
  useEffect(() => {
    if (scenarios.length > 0 && focusedBox.scenarioIndex >= 0) {
      const scenario = scenarios[focusedBox.scenarioIndex];
      if (scenario) {
        const boxKey = `${scenario.id}_${focusedBox.position}`;
        const boxElement = boxRefs.current[boxKey];
        if (boxElement) {
          boxElement.focus();
        }
      }
    }
  }, [focusedBox, scenarios]);

  // Hide focus indicator after 10 seconds of inactivity
  const resetInactivityTimer = React.useCallback(() => {
    setShowFocusIndicator(true);
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }
    inactivityTimeoutRef.current = setTimeout(() => {
      setShowFocusIndicator(false);
    }, 10000); // 10 seconds
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
    };
  }, []);

  // Show focus indicator when focusedBox changes (from arrow key navigation)
  useEffect(() => {
    if (showFocusIndicator === false) {
      resetInactivityTimer();
    }
  }, [focusedBox]);

  // Get the active scenario by index
  const activeScenario = scenarios[activeScenarioIndex] || scenarios[0];

  // Add scenario handler (adds to activeMap)
  const handleAddScenario = React.useCallback(() => {
    if (maxScenariosDemo && scenarios.length >= maxScenariosDemo) return;
    const slotKey = `General_${Date.now()}`;
    initializeMap(slotKey);
    setBoardMaps(prev => [...prev, slotKey]);
    setBoardLabels(prev => [...prev, 'General']);
  }, [initializeMap, maxScenariosDemo, scenarios.length]);

  // useEffect to handle pending copy operation when new scenario is added
  useEffect(() => {
    if (pendingCopyToScenarioId && copySourceScenario) {
      const targetMap = pendingCopyMapName || activeMap;
      const targetMapData = mapDrafts[targetMap] || {};
      const currentScenarios = pickMode === 'first'
        ? (targetMapData.scenariosFirstPick || [])
        : (targetMapData.scenariosSecondPick || []);
      const newScenario = currentScenarios.find(s => s.id === pendingCopyToScenarioId);

      if (newScenario) {
        const updateData = {
          picks: copySourceScenario.picks ? copySourceScenario.picks.map(p => p ? { ...p } : null) : Array(24).fill(null),
          title: copySourceScenario.title ? `${copySourceScenario.title} (copy)` : '',
          practiceMatchups: copySourceScenario.practiceMatchups ? { ...copySourceScenario.practiceMatchups } : {},
          swapSides: copySourceScenario.swapSides || false,
        };

        if (copySourceScenario.fromMatchPull) {
          updateData.fromMatchPull = true;
          updateData.matchColorIndex = copySourceScenario.matchColorIndex;
        }

        updateScenario(pickMode, newScenario.id, updateData, targetMap);

        setDroppedScenarioId(`${targetMap}_${newScenario.id}`);

        setTimeout(() => {
          const scenarioEl = scenarioRefs.current[`${targetMap}_${newScenario.id}`];
          if (scenarioEl) {
            scenarioEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
          }
          setTimeout(() => setDroppedScenarioId(null), 600);
        }, 100);

        setPendingCopyToScenarioId(null);
        setPendingCopyMapName(null);
        setCopySourceScenario(null);
      }
    }
  }, [pendingCopyToScenarioId, copySourceScenario, mapDrafts, activeMap, pendingCopyMapName, pickMode, updateScenario]);

  // Handle swap button for scenario
  const handleSwapSides = (scenario, mapName) => {
    const newSwapState = !(scenario.swapSides || false);
    updateScenario(pickMode, scenario.id, { swapSides: newSwapState }, mapName);
  };

  // Update a scenario's picks in the active map
  const updateScenarioPicks = (scenarioId, newPicks) => updateScenario(pickMode, scenarioId, { picks: newPicks }, activeMap);

  // ---------- Counter-relation connectors ----------
  const isReadOnly = isSimulatorView || (isSharedView && sharedPermission !== 'edit') || isDisabledDemo;

  const addCounterConnector = useCallback((mapName, scenarioId, fromPos, toPos) => {
    if (fromPos === toPos) return;
    const list = mapDrafts[mapName]?.[pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick'] || [];
    const sc = list.find(s => s.id === scenarioId);
    if (!sc) return;
    const existing = Array.isArray(sc.counterConnectors) ? sc.counterConnectors : [];
    if (existing.some(c => c.from === fromPos && c.to === toPos)) return;
    updateScenario(pickMode, scenarioId, { counterConnectors: [...existing, { from: fromPos, to: toPos }] }, mapName);
  }, [pickMode, mapDrafts, updateScenario]);

  const removeCounterConnector = useCallback((mapName, scenarioId, index) => {
    const list = mapDrafts[mapName]?.[pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick'] || [];
    const sc = list.find(s => s.id === scenarioId);
    if (!sc || !Array.isArray(sc.counterConnectors)) return;
    const next = sc.counterConnectors.filter((_, i) => i !== index);
    updateScenario(pickMode, scenarioId, { counterConnectors: next }, mapName);
  }, [pickMode, mapDrafts, updateScenario]);

  const handleConnectorHandleMouseDown = useCallback((e, scenarioId, position, mapName) => {
    if (isReadOnly) return;
    e.stopPropagation();
    e.preventDefault();
    setConnectorDrag({ scenarioId, mapName, fromPosition: position, mouseX: e.clientX, mouseY: e.clientY });
  }, [isReadOnly]);

  // Long-press to start a connector drag on touch devices.
  // Press a filled hero ~450ms without moving -> connector drag begins; slide finger to target and release.
  const longPressTimerRef = useRef(null);
  const touchStartRef = useRef(null);

  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartRef.current = null;
  }, []);

  const handleBoxTouchStart = useCallback((e, scenarioId, position, isFilled, mapName) => {
    if (isReadOnly || !isFilled) return;
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    touchStartRef.current = { scenarioId, position, mapName, x: t.clientX, y: t.clientY };
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      const start = touchStartRef.current;
      if (!start) return;
      if (window.navigator.vibrate) window.navigator.vibrate(35);
      // Seed mouseX/Y from the source box's bbox center so the arrow starts zero-length.
      // Touch clientX/Y and getBoundingClientRect live in different coord spaces under
      // CSS `zoom: 0.72` on mobile portrait (Safari iOS).
      const boxEl = boxRefs.current[`${start.scenarioId}_${start.position}`];
      let mx = start.x, my = start.y;
      if (boxEl) {
        const r = boxEl.getBoundingClientRect();
        mx = r.left + r.width / 2;
        my = r.top + r.height / 2;
      }
      setConnectorDrag({ scenarioId: start.scenarioId, mapName: start.mapName, fromPosition: start.position, mouseX: mx, mouseY: my });
    }, 450);
  }, [isReadOnly]);

  const handleBoxTouchMove = useCallback((e) => {
    if (!longPressTimerRef.current) return;
    const start = touchStartRef.current;
    if (!start || e.touches.length === 0) return;
    const t = e.touches[0];
    if (Math.abs(t.clientX - start.x) > 10 || Math.abs(t.clientY - start.y) > 10) {
      cancelLongPress();
    }
  }, [cancelLongPress]);

  // Lock page scroll + disable touch-action while a connector drag is active.
  useEffect(() => {
    if (!connectorDrag) return;
    const prevTouchAction = document.body.style.touchAction;
    const prevOverscroll = document.body.style.overscrollBehavior;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.touchAction = 'none';
    document.body.style.overscrollBehavior = 'none';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.touchAction = prevTouchAction;
      document.body.style.overscrollBehavior = prevOverscroll;
      document.body.style.userSelect = prevUserSelect;
    };
  }, [connectorDrag ? connectorDrag.scenarioId : null]); // eslint-disable-line react-hooks/exhaustive-deps

  // Document-level drag tracking (mouse + touch)
  useEffect(() => {
    if (!connectorDrag) return;

    const tryCommit = (clientX, clientY) => {
      const el = document.elementFromPoint(clientX, clientY);
      const boxEl = el && el.closest && el.closest('[data-connector-target="true"]');
      if (boxEl) {
        const sid = Number(boxEl.getAttribute('data-scenario-id'));
        const pos = Number(boxEl.getAttribute('data-position'));
        const dragSid = Number(connectorDrag.scenarioId);
        if (Number.isFinite(sid) && Number.isFinite(pos) && pos > 0 && sid === dragSid) {
          addCounterConnector(connectorDrag.mapName, connectorDrag.scenarioId, connectorDrag.fromPosition, pos);
        }
      }
    };

    const onMove = (e) => {
      setConnectorDrag(prev => prev ? { ...prev, mouseX: e.clientX, mouseY: e.clientY } : prev);
    };
    const onUp = (e) => {
      tryCommit(e.clientX, e.clientY);
      setConnectorDrag(null);
    };
    const onTouchMove = (e) => {
      if (e.touches.length === 0) return;
      if (e.cancelable) e.preventDefault();
      const t = e.touches[0];
      // Snap arrow tip to target box center via elementFromPoint to avoid coord space
      // mismatch between touch.clientX/Y and getBoundingClientRect under CSS `zoom`.
      const el = document.elementFromPoint(t.clientX, t.clientY);
      const boxEl = el && el.closest && el.closest('[data-connector-target="true"]');
      if (boxEl) {
        const r = boxEl.getBoundingClientRect();
        const cx = r.left + r.width / 2;
        const cy = r.top + r.height / 2;
        setConnectorDrag(prev => prev ? { ...prev, mouseX: cx, mouseY: cy } : prev);
      }
    };
    const onTouchEnd = (e) => {
      const t = e.changedTouches && e.changedTouches[0];
      if (t) tryCommit(t.clientX, t.clientY);
      setConnectorDrag(null);
    };
    const onTouchCancel = () => setConnectorDrag(null);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', onTouchCancel);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [connectorDrag, addCounterConnector]);

  // Re-measure connectors on window resize / scroll
  useEffect(() => {
    const bump = () => setConnectorTick(t => t + 1);
    window.addEventListener('resize', bump);
    window.addEventListener('scroll', bump, true);
    return () => {
      window.removeEventListener('resize', bump);
      window.removeEventListener('scroll', bump, true);
    };
  }, []);

  // Bump tick after every commit so SVGs recompute against the latest DOM rects.
  useLayoutEffect(() => {
    setConnectorTick(t => t + 1);
  }, [mapDrafts, pickMode]);

  // Forehead connector drag tracking
  useEffect(() => {
    if (!foreheadConnectorDrag) return;
    const onMove = (e) => {
      setForeheadConnectorDrag(prev => prev ? { ...prev, mouseX: e.clientX, mouseY: e.clientY } : prev);
    };
    const onUp = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const boxEl = el && el.closest && el.closest('[data-forehead-target="true"]');
      if (boxEl) {
        const toIdx = Number(boxEl.getAttribute('data-forehead-idx'));
        if (Number.isFinite(toIdx) && toIdx !== foreheadConnectorDrag.fromIdx) {
          setForeheadConnectors(prev => [...prev, { from: foreheadConnectorDrag.fromIdx, to: toIdx }]);
        }
      }
      setForeheadConnectorDrag(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [foreheadConnectorDrag, setForeheadConnectors]);

  // Handle box click to show hero selection popup
  const handleBoxClick = (scenarioIndex, position) => {
    if (isDisabledDemo) return;
    setIsForeheadMode(false);
    setActiveScenarioIndex(scenarioIndex);
    setSelectedBox(position);
    setShowPopup(true);
    if (selectedPositions.size > 0) {
      setSelectedPositions(new Set());
    }
  };

  // Handle hero selection from popup
  const handleHeroSelect = (hero) => {
    if (selectedBox === -1) return;

    const completeHero = {
      id: hero.id,
      name: hero.name,
      imageUrl: hero.imageUrl || '',
      attributes: hero.attributes || {}
    };

    if (isForeheadMode) {
      setForeheadPicks(prev => {
        const next = [...prev];
        if (preventDuplicates) {
          for (let i = 0; i < next.length; i++) {
            if (i !== selectedBox - 1 && next[i]?.hero?.id === completeHero.id) {
              next[i] = null;
            }
          }
        }
        next[selectedBox - 1] = { hero: completeHero, isBanned: false };
        return next;
      });
      setShowPopup(false);
      return;
    }

    if (!activeScenario) return;

    const newPicks = activeScenario.picks ?
      [...activeScenario.picks] :
      Array(24).fill(null);

    // Duplicate prevention
    if (preventDuplicates) {
      for (let i = 0; i < newPicks.length; i++) {
        const existingPick = newPicks[i];
        const existingPosition = i + 1;
        if (existingPosition === selectedBox) continue;

        const isDuplicateById = existingPick?.hero?.id && existingPick.hero.id === completeHero.id;
        const isDuplicateByName = existingPick?.hero?.name && existingPick.hero.name === completeHero.name;

        if (isDuplicateById || isDuplicateByName) {
          newPicks[i] = null;
          break;
        }
      }
    }

    newPicks[selectedBox - 1] = { hero: completeHero, isBanned: false };

    updateScenarioPicks(activeScenario.id, newPicks);
    setShowPopup(false);

    setTimeout(() => {
      const scenario = scenarios[activeScenarioIndex];
      if (scenario) {
        const boxKey = `${activeSlotIndex}_${scenario.id}_${selectedBox}`;
        const boxElement = boxRefs.current[boxKey];
        if (boxElement) boxElement.focus();
      }
    }, 100);
  };

  // Handle toggling a hero as banned
  const toggleBan = () => {
    if (selectedBox === -1 || !activeScenario || !activeScenario.picks[selectedBox - 1]) return;
    const newPicks = [...activeScenario.picks];
    newPicks[selectedBox - 1] = {
      ...newPicks[selectedBox - 1],
      isBanned: !newPicks[selectedBox - 1].isBanned
    };
    updateScenarioPicks(activeScenario.id, newPicks);
  };

  // Handle removing a hero
  const removeHero = () => {
    if (selectedBox === -1) return;
    if (isForeheadMode) {
      const idx = selectedBox - 1;
      setForeheadPicks(prev => { const next = [...prev]; next[idx] = null; return next; });
      setForeheadConnectors(prev => prev.filter(c => c.from !== idx && c.to !== idx));
      setShowPopup(false);
      return;
    }
    if (!activeScenario) return;
    const newPicks = [...activeScenario.picks];
    newPicks[selectedBox - 1] = null;
    updateScenarioPicks(activeScenario.id, newPicks);
    setShowPopup(false);
  };

  // Handle right-click on hero box - remove hero immediately
  const handleContextMenu = (e, scenarioId, position, mapName) => {
    e.preventDefault();
    e.stopPropagation();

    const mapScenarios = mapDrafts[mapName]?.[pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick'] || [];
    const scenario = mapScenarios.find(s => s.id === scenarioId);
    const pick = scenario?.picks?.[position - 1];

    if (pick?.hero) {
      const newPicks = [...scenario.picks];
      newPicks[position - 1] = null;
      updateScenario(pickMode, scenarioId, { picks: newPicks }, mapName);
    }
  };

  // Handle Cmd+Click to duplicate scenario and open hero selector
  const handleCmdClick = (e, scenarioId, position, scenarioIndex, mapName) => {
    if (!e.ctrlKey && !e.metaKey) return false;

    duplicateScenario(pickMode, scenarioId, mapName);

    setTimeout(() => {
      const newScenarioIndex = scenarioIndex + 1;
      setActiveScenarioIndex(newScenarioIndex);
      setSelectedBox(position);
      setShowPopup(true);
      setHeroSearch('');
    }, 100);

    return true;
  };

  // Arrow key grid navigation in hero selector
  useEffect(() => {
    if (!showPopup) return;

    const handler = (e) => {
      const GRID_COLUMNS = 4;

      setFocusedHeroIndex(currentIndex => {
        if (currentIndex < 0) return currentIndex;

        const totalHeroes = filteredHeroes.length;
        let newIndex = currentIndex;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const potentialIndex = currentIndex + GRID_COLUMNS;
          if (potentialIndex < totalHeroes) newIndex = potentialIndex;
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (currentIndex < GRID_COLUMNS) {
            if (searchInputRef.current) searchInputRef.current.focus();
            return -1;
          }
          newIndex = currentIndex - GRID_COLUMNS;
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          const currentRow = Math.floor(currentIndex / GRID_COLUMNS);
          const potentialIndex = currentIndex - 1;
          const potentialRow = Math.floor(potentialIndex / GRID_COLUMNS);
          if (potentialIndex >= 0 && potentialRow === currentRow) newIndex = potentialIndex;
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          const currentRow = Math.floor(currentIndex / GRID_COLUMNS);
          const potentialIndex = currentIndex + 1;
          const potentialRow = Math.floor(potentialIndex / GRID_COLUMNS);
          if (potentialIndex < totalHeroes && potentialRow === currentRow) newIndex = potentialIndex;
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (currentIndex >= 0 && currentIndex < totalHeroes) {
            handleHeroSelect(filteredHeroes[currentIndex]);
          }
          return currentIndex;
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setShowPopup(false);
          return -1;
        }

        if (newIndex !== currentIndex && filteredHeroes[newIndex]) {
          setTimeout(() => {
            const heroElement = heroRefs.current[filteredHeroes[newIndex]?.id];
            if (heroElement) heroElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          }, 0);
        }

        return newIndex;
      });
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showPopup, filteredHeroes, handleHeroSelect]);

  // Handle keyboard events in search input
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (focusedHeroIndex >= 0 && focusedHeroIndex < filteredHeroes.length) {
        const hero = filteredHeroes[focusedHeroIndex];
        setTimeout(() => handleHeroSelect(hero), 0);
      } else if (filteredHeroes.length > 0) {
        const hero = filteredHeroes[0];
        setTimeout(() => handleHeroSelect(hero), 0);
      }
    } else if (e.key === 'Escape') {
      setShowPopup(false);
      setFocusedHeroIndex(-1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      if (filteredHeroes.length > 0) {
        setFocusedHeroIndex(0);
        if (searchInputRef.current) searchInputRef.current.blur();
        setTimeout(() => {
          const heroElement = heroRefs.current[filteredHeroes[0].id];
          if (heroElement) heroElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }, 0);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      return;
    }
  };

  // Save draft
  const handleSaveDraft = () => {
    saveDraft({
      teamName,
      tournament,
      stage,
      date: draftDate,
      title: teamName ? `Draft for ${teamName}` : `Draft ${new Date().toLocaleDateString()}`,
      savedAt: new Date().toISOString(),
    });
  };

  // Handle new draft button
  const handleClearNonGeneralMaps = () => {
    setBoardMaps(['General']);
    setBoardLabels(['General']);
  };

  const handleAddAllMaps = () => {
    resetNonGeneralMapDrafts();
    setBoardMaps([...MAP_OPTIONS]);
    setBoardLabels([...MAP_OPTIONS]);
  };

  const handleNewDraft = () => {
    if (hasUnsavedChanges) {
      setPendingAction({ type: 'newDraft' });
      setShowConfirmation(true);
    } else {
      newDraft();
    }
  };

  // Handle confirmation dialog actions
  const handleConfirmSave = async () => {
    try {
      handleSaveDraft();
      newDraft(true);
    } catch (error) {
      console.error('Error saving draft:', error);
    } finally {
      setShowConfirmation(false);
      setPendingAction(null);
    }
  };

  const handleConfirmDiscard = () => {
    try {
      newDraft(true);
    } catch (error) {
      console.error('Error discarding:', error);
    } finally {
      setShowConfirmation(false);
      setPendingAction(null);
    }
  };

  const handleConfirmCancel = () => {
    setShowConfirmation(false);
    setPendingAction(null);
  };

  // Handle copy scenario to other pick mode
  const handleCopyToOtherMode = (scenario, currentPickMode, mapName) => {
    const targetPickMode = currentPickMode === 'first' ? 'second' : 'first';
    const targetMapData = mapDrafts[mapName] || {};
    const currentTargetScenarios = targetPickMode === 'first'
      ? (targetMapData.scenariosFirstPick || [])
      : (targetMapData.scenariosSecondPick || []);

    const maxId = currentTargetScenarios.length > 0 ? Math.max(...currentTargetScenarios.map(s => s.id)) : -1;
    const newScenarioId = maxId + 1;

    setPendingCopyToScenarioId(newScenarioId);
    setPendingCopyMapName(mapName);
    setCopySourceScenario(scenario);
    setCopySourcePickMode(currentPickMode);

    setPickMode(targetPickMode);
    addScenario(targetPickMode, mapName);
  };

  // Handle global "Copy From" button click - show overlay
  const handleCopyFromClick = () => {
    setShowScenarioSelection(true);
  };

  // Handle scenario selection from overlay - add as new scenario to current mode on the source's map
  const handleSelectSourceScenario = (sourceScenario, sourcePickMode, sourceMapName) => {
    const targetMap = sourceMapName || activeMap;
    const targetMapData = mapDrafts[targetMap] || {};
    const currentScenarios = pickMode === 'first'
      ? (targetMapData.scenariosFirstPick || [])
      : (targetMapData.scenariosSecondPick || []);
    const maxId = currentScenarios.length > 0 ? Math.max(...currentScenarios.map(s => s.id)) : -1;
    const newScenarioId = maxId + 1;

    setPendingCopyToScenarioId(newScenarioId);
    setPendingCopyMapName(targetMap);
    setCopySourceScenario(sourceScenario);
    setCopySourcePickMode(sourcePickMode);

    addScenario(pickMode, targetMap);
    setShowScenarioSelection(false);
  };

  // Handle cancel scenario selection
  const handleCancelScenarioSelection = () => {
    setShowScenarioSelection(false);
  };

  // Handle ESC key to close overlays
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        if (showScenarioSelection) handleCancelScenarioSelection();
        if (showBothOverlay) setShowBothOverlay(false);
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [showScenarioSelection, showBothOverlay]);

  // Drag and drop functionality (for JSON files)
  const handleDragOver = (e) => {
    if (isScenarioDragging) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (isScenarioDragging) return;
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e) => {
    if (isScenarioDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const file = files[0];
    if (!file.name.toLowerCase().endsWith('.json')) return;

    try {
      const text = await file.text();
      let draftData;
      try {
        draftData = JSON.parse(text);
      } catch (parseError) {
        console.warn('Invalid JSON file format');
        return;
      }

      if (!draftData || (!draftData.id && !draftData.scenariosFirstPick && !draftData.scenariosSecondPick)) {
        console.warn('Not a valid draft file');
        return;
      }

      if (!draftData.id) {
        draftData.id = `draft_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      }

      draftData.createdAt = draftData.createdAt || draftData.date || new Date().toISOString();
      draftData.updatedAt = draftData.updatedAt || draftData.createdAt;

      if (!currentDraftId) {
        await importDraftData(draftData, true);
      } else {
        // Ask user: merge or replace
        const choice = window.confirm(
          'A draft is already loaded.\n\nClick OK to REPLACE the current draft, or Cancel to MERGE (add scenarios from dropped file).'
        );
        if (choice) {
          await importDraftData(draftData, true);
        } else {
          mergeDraftData(draftData);
        }
      }
    } catch (err) {
      console.error('Error importing dropped file:', err);
    }
  };

  // Scenario drag-and-drop handlers
  const handleScenarioDragStart = (e, scenarioId, mapName) => {
    if (!e.target.closest('[data-drag-handle]')) {
      e.preventDefault();
      return;
    }

    setDraggedScenarioId(scenarioId);
    setDraggedScenarioMap(mapName);
    setIsScenarioDragging(true);
    e.dataTransfer.effectAllowed = 'move';

    const container = e.target.closest('.draft-board');
    if (container) {
      const wrapper = document.createElement('div');
      wrapper.style.position = 'absolute';
      wrapper.style.top = '-10000px';
      wrapper.style.left = '-10000px';
      wrapper.style.width = container.offsetWidth + 'px';
      wrapper.style.padding = '3px';
      wrapper.style.backgroundColor = '#2196F3';
      wrapper.style.borderRadius = '15px';
      wrapper.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.8), 0 0 20px rgba(33, 150, 243, 0.6)';

      const clone = container.cloneNode(true);
      clone.style.border = 'none';
      clone.style.borderRadius = '12px';
      clone.style.backgroundColor = '#0b0d14';
      clone.style.backgroundImage = 'linear-gradient(to bottom, #161926, #0b0d14)';
      clone.style.width = '100%';
      clone.style.boxShadow = 'none';

      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);

      e.dataTransfer.setDragImage(wrapper, container.offsetWidth / 2, 20);

      setTimeout(() => {
        if (document.body.contains(wrapper)) document.body.removeChild(wrapper);
      }, 0);
    }
  };

  const handleScenarioDragOver = (e, scenarioId, mapName) => {
    if (!isScenarioDragging) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';

    if (draggedScenarioId !== null && (draggedScenarioId !== scenarioId || draggedScenarioMap !== mapName)) {
      const refKey = `${mapName}_${scenarioId}`;
      const targetElement = scenarioRefs.current[refKey];
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        const midpoint = rect.left + rect.width / 2;
        const isLeftHalf = e.clientX < midpoint;
        setDragOverScenarioId({ id: scenarioId, mapName, insertBefore: isLeftHalf });
      } else {
        setDragOverScenarioId({ id: scenarioId, mapName, insertBefore: false });
      }
    }
  };

  const handleScenarioDragLeave = (e, scenarioId, mapName) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      const currentDragOver = dragOverScenarioId;
      if (currentDragOver && typeof currentDragOver === 'object' &&
          currentDragOver.id === scenarioId && currentDragOver.mapName === mapName) {
        setDragOverScenarioId(null);
      }
    }
  };

  const handleScenarioDragEnd = (e) => {
    if (draggedScenarioId !== null && dragOverScenarioId !== null) {
      const targetId = dragOverScenarioId.id;
      const targetMapName = dragOverScenarioId.mapName;
      const insertBefore = dragOverScenarioId.insertBefore;
      const sourceMap = draggedScenarioMap;

      if (sourceMap === targetMapName) {
        // Same map — reorder
        const sourceScenarios = mapDrafts[sourceMap]?.[pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick'] || [];
        const fromIndex = sourceScenarios.findIndex(s => s.id === draggedScenarioId);
        const targetIndex = sourceScenarios.findIndex(s => s.id === targetId);

        if (fromIndex !== -1 && targetIndex !== -1 && fromIndex !== targetIndex) {
          let toIndex = targetIndex;
          if (!insertBefore) {
            toIndex = fromIndex < targetIndex ? targetIndex : targetIndex + 1;
          } else {
            toIndex = fromIndex < targetIndex ? targetIndex - 1 : targetIndex;
          }
          reorderScenarios(pickMode, fromIndex, toIndex, sourceMap);
        }
      } else {
        // Cross-map — move scenario to target map
        const targetScenarios = mapDrafts[targetMapName]?.[pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick'] || [];
        const targetIndex = targetScenarios.findIndex(s => s.id === targetId);
        const insertAt = insertBefore ? targetIndex : targetIndex + 1;
        moveScenarioToMap(pickMode, draggedScenarioId, sourceMap, targetMapName, insertAt);

        // If source slot will be empty after the move, remove it from the board
        const sourceScenarios = mapDrafts[sourceMap]?.[pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick'] || [];
        if (sourceScenarios.length <= 1) {
          const sourceSlotIndex = boardMaps.indexOf(sourceMap);
          if (sourceSlotIndex !== -1) {
            setBoardMaps(prev => prev.filter((_, i) => i !== sourceSlotIndex));
            setBoardLabels(prev => prev.filter((_, i) => i !== sourceSlotIndex));
          }
        }
      }

      setDroppedScenarioId(`${targetMapName}_${draggedScenarioId}`);
      setTimeout(() => setDroppedScenarioId(null), 600);
      setRipplePosition({ x: e.clientX, y: e.clientY, show: true });
      setTimeout(() => setRipplePosition({ x: 0, y: 0, show: false }), 1200);
    }
    setDraggedScenarioId(null);
    setDraggedScenarioMap(null);
    setDragOverScenarioId(null);
    setIsScenarioDragging(false);
  };

  // Prevent default drag behavior on the entire page
  useEffect(() => {
    const preventDefault = (e) => e.preventDefault();
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);
    return () => {
      document.removeEventListener('dragover', preventDefault);
      document.removeEventListener('drop', preventDefault);
    };
  }, []);

  // Multi-select handlers
  const handleMouseDown = (e) => {
    if (isScenarioDragging ||
      e.button !== 0 ||
      e.target.closest('[data-position]') ||
      e.target.closest('[data-drag-handle]') ||
      e.target.tagName === 'SELECT' ||
      e.target.tagName === 'BUTTON' ||
      e.target.tagName === 'INPUT' ||
      e.target.tagName === 'OPTION') {
      return;
    }
    // Record which slot and map this drag started from
    const slotEl = e.target.closest('[data-slot-idx]');
    selectionSlotRef.current = slotEl ? slotEl.getAttribute('data-slot-idx') : null;
    selectionMapRef.current = slotEl ? slotEl.getAttribute('data-map-name') : null;
    setIsSelecting(true);
    setSelectionStart({ x: e.clientX, y: e.clientY });
    setSelectionEnd({ x: e.clientX, y: e.clientY });
    setSelectedPositions(new Set());
  };

  const handleMouseMove = (e) => {
    if (!isSelecting || isScenarioDragging) return;
    setSelectionEnd({ x: e.clientX, y: e.clientY });

    const selectionBox = {
      left: Math.min(selectionStart.x, e.clientX),
      top: Math.min(selectionStart.y, e.clientY),
      right: Math.max(selectionStart.x, e.clientX),
      bottom: Math.max(selectionStart.y, e.clientY)
    };

    const newSelectedPositions = new Set();

    document.querySelectorAll('[data-box-key]').forEach(boxElement => {
      const rect = boxElement.getBoundingClientRect();
      // Skip elements inside a display:none container (desktop or mobile when inactive)
      if (rect.width === 0 && rect.height === 0) return;
      const intersects = !(
        selectionBox.right < rect.left ||
        selectionBox.left > rect.right ||
        selectionBox.bottom < rect.top ||
        selectionBox.top > rect.bottom
      );
      if (intersects) newSelectedPositions.add(boxElement.getAttribute('data-box-key'));
    });

    setSelectedPositions(newSelectedPositions);
  };

  const handleMouseUp = () => {
    setIsSelecting(false);
  };

  // Keyboard handler for type-to-search, navigation, copy/paste, delete
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isSimulatorView || isDisabledDemo) return;

      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      // Handle Undo (Ctrl+Z or Cmd+Z)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        undo();
        return;
      }

      // Handle Redo (Ctrl+Y or Cmd+Shift+Z or Cmd+Y)
      if (((e.ctrlKey || e.metaKey) && e.key === 'y') || ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z')) {
        e.preventDefault();
        e.stopPropagation();
        redo();
        return;
      }

      // Handle Save Draft (Ctrl+S or Cmd+S)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        e.stopPropagation();
        handleSaveDraft();
        return;
      }

      // Handle New Scenario (Ctrl+N or Cmd+N)
      if ((e.ctrlKey || e.metaKey) && e.key === 'n' && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        addScenario(pickMode);
        return;
      }

      // Handle Copy (Ctrl+C or Cmd+C)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C') && !showPopup) {
        e.preventDefault();
        e.stopPropagation();

        if (selectedPositions.size === 0 && focusedBox.scenarioIndex >= 0 && focusedBox.position > 0) {
          const scenario = scenarios[focusedBox.scenarioIndex];
          if (scenario) {
            const pick = scenario.picks?.[focusedBox.position - 1];
            setClipboard([{
              pick: pick ? { ...pick } : null,
              position: focusedBox.position,
              offset: 0
            }]);
          }
          return;
        }

        if (selectedPositions.size === 0) return;

        const positionsWithCoords = [];
        selectedPositions.forEach((key) => {
          const [keyMapName, scenarioId, position] = key.split('_');
          const boxElement = boxRefs.current[`${scenarioId}_${position}`];
          if (boxElement) {
            const rect = boxElement.getBoundingClientRect();
            positionsWithCoords.push({ key, keyMapName, scenarioId, position: parseInt(position), top: rect.top, left: rect.left });
          }
        });

        positionsWithCoords.sort((a, b) => {
          const topDiff = a.top - b.top;
          if (Math.abs(topDiff) < 5) return a.left - b.left;
          return topDiff;
        });

        const copiedData = [];
        const firstPosition = positionsWithCoords.length > 0 ? positionsWithCoords[0].position : null;

        positionsWithCoords.forEach(({ scenarioId, position }) => {
          const scenario = scenarios.find(s => String(s.id) === String(scenarioId));
          if (scenario) {
            const pick = scenario.picks ? scenario.picks[position - 1] : null;
            copiedData.push({ pick: pick ? { ...pick } : null, position, offset: position - firstPosition });
          }
        });

        if (copiedData.length > 0) setClipboard(copiedData);
        return;
      }

      // Handle Paste (Ctrl+V or Cmd+V)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V') && clipboard.length > 0 && !showPopup) {
        e.preventDefault();
        e.stopPropagation();

        const updates = new Map();

        if (selectedPositions.size > 0) {
          const selectedWithCoords = [];
          selectedPositions.forEach((key) => {
            const boxElement = boxRefs.current[key];
            if (boxElement) {
              const rect = boxElement.getBoundingClientRect();
              selectedWithCoords.push({ key, top: rect.top, left: rect.left });
            }
          });

          selectedWithCoords.sort((a, b) => {
            const topDiff = a.top - b.top;
            if (Math.abs(topDiff) < 5) return a.left - b.left;
            return topDiff;
          });

          const selectedArray = selectedWithCoords.map(item => item.key);

          selectedArray.forEach((key, index) => {
            const [keyMapName, scenarioId, position] = key.split('_');
            const keyMapScenarios = mapDrafts[keyMapName]?.[pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick'] || [];
            const scenario = keyMapScenarios.find(s => String(s.id) === String(scenarioId));
            if (scenario) {
              const updateKey = `${keyMapName}_${scenarioId}`;
              if (!updates.has(updateKey)) {
                updates.set(updateKey, { mapName: keyMapName, scenarioId, picks: scenario.picks ? [...scenario.picks] : Array(24).fill(null) });
              }
              const picks = updates.get(updateKey).picks;
              const posIndex = parseInt(position) - 1;
              const clipboardIndex = index % clipboard.length;
              picks[posIndex] = clipboard[clipboardIndex].pick ? { ...clipboard[clipboardIndex].pick } : null;
            }
          });
        } else if (focusedBox) {
          const scenario = scenarios[focusedBox.scenarioIndex];
          if (scenario) {
            const scenarioId = scenario.id;
            const picks = scenario.picks ? [...scenario.picks] : Array(24).fill(null);
            clipboard.forEach((clipboardItem) => {
              const targetPosition = focusedBox.position + clipboardItem.offset;
              if (targetPosition >= 1 && targetPosition <= 24) {
                picks[targetPosition - 1] = clipboardItem.pick ? { ...clipboardItem.pick } : null;
              }
            });
            updates.set(`${activeMap}_${scenarioId}`, { mapName: activeMap, scenarioId, picks });
          }
        }

        updates.forEach(({ mapName: updateMapName, scenarioId: updateScenarioId, picks }) => {
          const numId = typeof updateScenarioId === 'string' && !isNaN(updateScenarioId)
            ? Number(updateScenarioId)
            : updateScenarioId;
          updateScenario(pickMode, numId, { picks }, updateMapName);
        });
        return;
      }

      // Handle Delete on forehead multi-selection
      if ((e.key === 'Backspace' || e.key === 'Delete') && foreheadSelection.size > 0 && !showPopup) {
        e.preventDefault();
        e.stopPropagation();
        setForeheadPicks(prev => {
          const next = [...prev];
          foreheadSelection.forEach(idx => { next[idx] = null; });
          return next;
        });
        setForeheadConnectors(prev => prev.filter(c => !foreheadSelection.has(c.from) && !foreheadSelection.has(c.to)));
        setForeheadSelection(new Set());
        return;
      }

      // Handle Delete on focused box (no multi-selection)
      if ((e.key === 'Backspace' || e.key === 'Delete') && selectedPositions.size === 0 && !showPopup) {
        e.preventDefault();
        e.stopPropagation();

        const scenario = scenarios[focusedBox.scenarioIndex];
        if (scenario && scenario.picks && scenario.picks[focusedBox.position - 1]) {
          const newPicks = [...scenario.picks];
          newPicks[focusedBox.position - 1] = null;
          updateScenario(pickMode, scenario.id, { picks: newPicks }, activeMap);
        }
        return;
      }

      // Handle Delete with multi-selection
      if ((e.key === 'Backspace' || e.key === 'Delete') && selectedPositions.size > 0 && !showPopup) {
        e.preventDefault();
        e.stopPropagation();

        const updates = new Map();
        selectedPositions.forEach((key) => {
          const [keyMapName, scenarioId, position] = key.split('_');
          const keyMapScenarios = mapDrafts[keyMapName]?.[pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick'] || [];
          const scenario = keyMapScenarios.find(s => String(s.id) === String(scenarioId));
          if (scenario) {
            const updateKey = `${keyMapName}_${scenarioId}`;
            if (!updates.has(updateKey)) {
              updates.set(updateKey, { mapName: keyMapName, scenarioId, picks: scenario.picks ? [...scenario.picks] : Array(24).fill(null) });
            }
            updates.get(updateKey).picks[parseInt(position) - 1] = null;
          }
        });

        updates.forEach(({ mapName: updateMapName, scenarioId: updateScenarioId, picks }) => {
          const numId = typeof updateScenarioId === 'string' && !isNaN(updateScenarioId)
            ? Number(updateScenarioId)
            : updateScenarioId;
          updateScenario(pickMode, numId, { picks }, updateMapName);
        });

        setSelectedPositions(new Set());
        return;
      }

      // Handle Enter key - open hero selector on focused box
      if (e.key === 'Enter' && !showPopup) {
        e.preventDefault();
        e.stopPropagation();
        handleBoxClick(focusedBox.scenarioIndex, focusedBox.position);
        return;
      }

      // Arrow key navigation (when not in popup)
      if (!showPopup && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        resetInactivityTimer();

        // MLBB column layout (top to bottom visual order)
        const radiantColumn = [1, 4, 7, 14, 15, 18, 27, 22, 23, 20];
        const direColumn = [2, 3, 5, 13, 16, 17, 26, 21, 24, 25];

        // Horizontal navigation mapping (side-by-side positions)
        const horizontalPairs = {
          1: 2, 4: 3, 7: 5, 14: 13, 15: 16, 18: 17, 27: 26, 22: 21, 23: 24, 20: 25,
          2: 1, 3: 4, 5: 7, 13: 14, 16: 15, 17: 18, 26: 27, 21: 22, 24: 23, 25: 20
        };

        // Ctrl/Cmd + Shift + Arrow for multi-select (Excel-style)
        if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
          const currentScenario = scenarios[focusedBox.scenarioIndex];
          if (!currentScenario) return;

          const newSelection = new Set(selectedPositions);

          if (e.key === 'ArrowDown') {
            const inRadiantCol = radiantColumn.includes(focusedBox.position);
            const column = inRadiantCol ? radiantColumn : direColumn;
            const currentIndex = column.indexOf(focusedBox.position);

            let furthestSelectedIndex = currentIndex;
            for (let i = currentIndex; i < column.length; i++) {
              const key = `${currentScenario.id}_${column[i]}`;
              if (newSelection.has(key)) {
                furthestSelectedIndex = i;
              } else {
                break;
              }
            }

            let endIndex = furthestSelectedIndex;
            const nextIndex = furthestSelectedIndex + 1;
            if (nextIndex < column.length) {
              const nextPick = currentScenario.picks?.[column[nextIndex] - 1];
              if (nextPick?.hero) {
                for (let i = nextIndex; i < column.length; i++) {
                  const pick = currentScenario.picks?.[column[i] - 1];
                  if (!pick?.hero) break;
                  endIndex = i;
                }
              } else {
                let foundFilled = false;
                for (let i = nextIndex; i < column.length; i++) {
                  const pick = currentScenario.picks?.[column[i] - 1];
                  if (pick?.hero) { endIndex = i; foundFilled = true; break; }
                }
                if (!foundFilled && nextIndex < column.length) endIndex = nextIndex;
              }
            }

            for (let i = currentIndex; i <= endIndex; i++) {
              newSelection.add(`${currentScenario.id}_${column[i]}`);
            }
            setSelectedPositions(newSelection);
            if (endIndex > currentIndex) {
              setFocusedBox({ scenarioIndex: focusedBox.scenarioIndex, position: column[endIndex] });
            }
            return;
          } else if (e.key === 'ArrowUp') {
            const inRadiantCol = radiantColumn.includes(focusedBox.position);
            const column = inRadiantCol ? radiantColumn : direColumn;
            const currentIndex = column.indexOf(focusedBox.position);

            let furthestSelectedIndex = currentIndex;
            for (let i = currentIndex; i >= 0; i--) {
              const key = `${currentScenario.id}_${column[i]}`;
              if (newSelection.has(key)) {
                furthestSelectedIndex = i;
              } else {
                break;
              }
            }

            let startIndex = furthestSelectedIndex;
            const prevIndex = furthestSelectedIndex - 1;
            if (prevIndex >= 0) {
              const prevPick = currentScenario.picks?.[column[prevIndex] - 1];
              if (prevPick?.hero) {
                for (let i = prevIndex; i >= 0; i--) {
                  const pick = currentScenario.picks?.[column[i] - 1];
                  if (!pick?.hero) break;
                  startIndex = i;
                }
              } else {
                let foundFilled = false;
                for (let i = prevIndex; i >= 0; i--) {
                  const pick = currentScenario.picks?.[column[i] - 1];
                  if (pick?.hero) { startIndex = i; foundFilled = true; break; }
                }
                if (!foundFilled && prevIndex >= 0) startIndex = prevIndex;
              }
            }

            for (let i = startIndex; i <= currentIndex; i++) {
              newSelection.add(`${currentScenario.id}_${column[i]}`);
            }
            setSelectedPositions(newSelection);
            if (startIndex < currentIndex) {
              setFocusedBox({ scenarioIndex: focusedBox.scenarioIndex, position: column[startIndex] });
            }
            return;
          }
        }

        // Ctrl/Cmd + Arrow (without Shift) for scenario/column jump navigation
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
          if (e.key === 'ArrowLeft') {
            if (focusedBox.scenarioIndex > 0) {
              // Move to previous scenario within the same map column
              const newScenarioIndex = focusedBox.scenarioIndex - 1;
              setFocusedBox({ scenarioIndex: newScenarioIndex, position: focusedBox.position });
              const scenario = scenarios[newScenarioIndex];
              if (scenario) {
                const boxElement = boxRefs.current[`${scenario.id}_${focusedBox.position}`];
                if (boxElement) boxElement.focus();
              }
            } else {
              // At first scenario — cross into the previous map column's last scenario
              const prevSlotIndex = activeSlotIndex - 1;
              if (prevSlotIndex >= 0) {
                const prevMapName = boardMaps[prevSlotIndex];
                const prevScenarios = mapDrafts[prevMapName]?.[pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick'] || [];
                if (prevScenarios.length > 0) {
                  const newScenarioIndex = prevScenarios.length - 1;
                  setActiveMap(prevMapName);
                  setActiveSlotIndex(prevSlotIndex);
                  setFocusedBox({ scenarioIndex: newScenarioIndex, position: focusedBox.position });
                  const scenario = prevScenarios[newScenarioIndex];
                  if (scenario) {
                    const boxElement = boxRefs.current[`${scenario.id}_${focusedBox.position}`];
                    if (boxElement) boxElement.focus();
                  }
                }
              }
            }
            return;
          } else if (e.key === 'ArrowRight') {
            if (focusedBox.scenarioIndex < scenarios.length - 1) {
              // Move to next scenario within the same map column
              const newScenarioIndex = focusedBox.scenarioIndex + 1;
              setFocusedBox({ scenarioIndex: newScenarioIndex, position: focusedBox.position });
              const scenario = scenarios[newScenarioIndex];
              if (scenario) {
                const boxElement = boxRefs.current[`${scenario.id}_${focusedBox.position}`];
                if (boxElement) boxElement.focus();
              }
            } else {
              // At last scenario — cross into the next map column's first scenario
              const nextSlotIndex = activeSlotIndex + 1;
              if (nextSlotIndex < boardMaps.length) {
                const nextMapName = boardMaps[nextSlotIndex];
                const nextScenarios = mapDrafts[nextMapName]?.[pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick'] || [];
                if (nextScenarios.length > 0) {
                  setActiveMap(nextMapName);
                  setActiveSlotIndex(nextSlotIndex);
                  setFocusedBox({ scenarioIndex: 0, position: focusedBox.position });
                  const scenario = nextScenarios[0];
                  if (scenario) {
                    const boxElement = boxRefs.current[`${scenario.id}_${focusedBox.position}`];
                    if (boxElement) boxElement.focus();
                  }
                }
              }
            }
            return;
          } else if (e.key === 'ArrowUp') {
            const inRadiantCol = radiantColumn.includes(focusedBox.position);
            const column = inRadiantCol ? radiantColumn : direColumn;
            const scenario = scenarios[focusedBox.scenarioIndex];
            if (scenario && scenario.picks) {
              let newPosition = null;
              for (const pos of column) {
                const pick = scenario.picks[pos - 1];
                if (pick && pick.hero) { newPosition = pos; break; }
              }
              if (!newPosition) newPosition = column[0];
              setFocusedBox({ scenarioIndex: focusedBox.scenarioIndex, position: newPosition });
              const boxKey = `${scenario.id}_${newPosition}`;
              const boxElement = boxRefs.current[boxKey];
              if (boxElement) boxElement.focus();
            }
            return;
          } else if (e.key === 'ArrowDown') {
            const inRadiantCol = radiantColumn.includes(focusedBox.position);
            const column = inRadiantCol ? radiantColumn : direColumn;
            const scenario = scenarios[focusedBox.scenarioIndex];
            if (scenario && scenario.picks) {
              let newPosition = null;
              for (let i = column.length - 1; i >= 0; i--) {
                const pos = column[i];
                const pick = scenario.picks[pos - 1];
                if (pick && pick.hero) { newPosition = pos; break; }
              }
              if (!newPosition) newPosition = column[column.length - 1];
              setFocusedBox({ scenarioIndex: focusedBox.scenarioIndex, position: newPosition });
              const boxKey = `${scenario.id}_${newPosition}`;
              const boxElement = boxRefs.current[boxKey];
              if (boxElement) boxElement.focus();
            }
            return;
          }
        }

        // Regular arrow key navigation (no Ctrl/Cmd)
        const currentPos = focusedBox.position;
        let newPosition = currentPos;
        const currentScenario = scenarios[focusedBox.scenarioIndex];
        const isSwapped = currentScenario?.swapSides || false;

        if (desktopView === 'drafter') {
          // Drafter POV: bans are a horizontal row, picks are two vertical columns
          const usBans    = isSwapped ? [2, 3, 5, 26, 21]    : [1, 4, 7, 27, 22];
          const enemyBans = isSwapped ? [1, 4, 7, 27, 22]    : [2, 3, 5, 26, 21];
          const usPicks   = isSwapped ? [13, 16, 17, 24, 25] : [14, 15, 18, 23, 20];
          const enemyPicks= isSwapped ? [14, 15, 18, 23, 20] : [13, 16, 17, 24, 25];

          const inUsBans    = usBans.includes(currentPos);
          const inEnemyBans = enemyBans.includes(currentPos);
          const inUsPicks   = usPicks.includes(currentPos);
          const inEnemyPicks= enemyPicks.includes(currentPos);

          if (inUsBans || inEnemyBans) {
            // In the ban row — Left/Right navigate across both ban groups; Down jumps to picks
            const banRow = inUsBans ? usBans : enemyBans;
            const banIdx = banRow.indexOf(currentPos);
            if (e.key === 'ArrowLeft') {
              if (banIdx > 0) {
                newPosition = banRow[banIdx - 1];
              } else if (inEnemyBans) {
                // Cross from first enemy ban to last Us ban
                newPosition = usBans[usBans.length - 1];
              }
            } else if (e.key === 'ArrowRight') {
              if (banIdx < banRow.length - 1) {
                newPosition = banRow[banIdx + 1];
              } else if (inUsBans) {
                // Cross from last Us ban to first enemy ban
                newPosition = enemyBans[0];
              }
            } else if (e.key === 'ArrowDown') {
              newPosition = inUsBans ? usPicks[0] : enemyPicks[0];
            }
            // ArrowUp at top of ban row: no-op
          } else if (inUsPicks || inEnemyPicks) {
            // In pick columns — Up/Down within column; Up from first goes back to ban row
            const pickCol = inUsPicks ? usPicks : enemyPicks;
            const pickIdx = pickCol.indexOf(currentPos);
            if (e.key === 'ArrowDown' && pickIdx < pickCol.length - 1) {
              newPosition = pickCol[pickIdx + 1];
            } else if (e.key === 'ArrowUp') {
              if (pickIdx > 0) {
                newPosition = pickCol[pickIdx - 1];
              } else if (inUsPicks) {
                // Us picks column sits under the right end of Us bans — jump to last Us ban
                newPosition = usBans[usBans.length - 1];
              } else {
                // Enemy picks column sits under the left end of Enemy bans — jump to first enemy ban
                newPosition = enemyBans[0];
              }
            } else if (e.key === 'ArrowLeft' && inEnemyPicks) {
              newPosition = usPicks[pickIdx] ?? usPicks[0];
            } else if (e.key === 'ArrowRight' && inUsPicks) {
              newPosition = enemyPicks[pickIdx] ?? enemyPicks[0];
            }
          }
        } else {
          // Normal view navigation: two vertical columns with horizontal pairs
          const inRadiantCol = radiantColumn.includes(currentPos);
          const inDireCol = direColumn.includes(currentPos);

          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            const column = inRadiantCol ? radiantColumn : direColumn;
            const currentIndex = column.indexOf(currentPos);
            if (currentIndex !== -1) {
              if (e.key === 'ArrowUp' && currentIndex > 0) {
                newPosition = column[currentIndex - 1];
              } else if (e.key === 'ArrowDown' && currentIndex < column.length - 1) {
                newPosition = column[currentIndex + 1];
              }
            }
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            if (!isSwapped) {
              if (e.key === 'ArrowLeft' && inDireCol) {
                newPosition = horizontalPairs[currentPos] || currentPos;
              } else if (e.key === 'ArrowRight' && inRadiantCol) {
                newPosition = horizontalPairs[currentPos] || currentPos;
              }
            } else {
              if (e.key === 'ArrowLeft' && inRadiantCol) {
                newPosition = horizontalPairs[currentPos] || currentPos;
              } else if (e.key === 'ArrowRight' && inDireCol) {
                newPosition = horizontalPairs[currentPos] || currentPos;
              }
            }
          }
        }

        if (newPosition !== currentPos) {
          if (e.shiftKey) {
            const scenario = scenarios[focusedBox.scenarioIndex];
            if (scenario) {
              const newSelections = new Set(selectedPositions);
              if (newSelections.size === 0) {
                newSelections.add(`${activeMap}_${scenario.id}_${currentPos}`);
              }
              const boxKey = `${activeMap}_${scenario.id}_${newPosition}`;
              if (newSelections.has(boxKey)) {
                newSelections.delete(boxKey);
              } else {
                newSelections.add(boxKey);
              }
              setSelectedPositions(newSelections);
            }
          } else {
            if (selectedPositions.size > 0) {
              setSelectedPositions(new Set());
            }
          }

          setFocusedBox({ scenarioIndex: focusedBox.scenarioIndex, position: newPosition });
          const scenario = scenarios[focusedBox.scenarioIndex];
          if (scenario) {
            const boxKey = `${scenario.id}_${newPosition}`;
            const boxElement = boxRefs.current[boxKey];
            if (boxElement) boxElement.focus();
          }
        }
        return;
      }

      // Auto-focus search box when typing alphanumeric characters
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        const isTypingChar = e.key.length === 1 && /[a-zA-Z0-9 ]/.test(e.key);

        if (isTypingChar) {
          if (showPopup && selectedBox !== -1 && searchInputRef.current) {
            if (document.activeElement !== searchInputRef.current) {
              e.preventDefault();
              searchInputRef.current.focus();
              setHeroSearch(prev => prev + e.key);
              return;
            }
          }

          if (!showPopup && focusedBox && focusedBox.scenarioIndex >= 0 && focusedBox.position > 0) {
            e.preventDefault();
            e.stopPropagation();

            setHeroSearch(e.key);
            handleBoxClick(focusedBox.scenarioIndex, focusedBox.position);

            requestAnimationFrame(() => {
              if (searchInputRef.current) {
                searchInputRef.current.value = e.key;
                searchInputRef.current.focus();
                searchInputRef.current.setSelectionRange(1, 1);
              }
            });
            return;
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedPositions, foreheadSelection, setForeheadPicks, scenarios, pickMode, showPopup, clipboard, undo, redo, focusedBox, updateScenario, addScenario, resetInactivityTimer, desktopView]);

  // MLBB position arrays
  const radiantPositions = [1, 4, 7, 8, 10, 11, 14, 15, 18, 19, 22, 23, 20, 27];
  const banPositions = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12, 21, 22, 26, 27];

  // Create pick box for a given position, scenario index, map, and slotIndex
  const renderPickBox = React.useCallback((scenarioIndex, position, mapName, slotIdx, sizeOverride = null, widthOverride = null) => {
    const mapScenarioList = mapDrafts[mapName]?.[pickMode === 'first' ? 'scenariosFirstPick' : 'scenariosSecondPick'] || [];
    const scenario = mapScenarioList[scenarioIndex];
    if (!scenario) return null;

    const scenarioId = scenario.id;
    const isRadiant = radiantPositions.includes(position);
    const team = isRadiant ? 'Radiant' : 'Dire';
    const pick = scenario.picks ? scenario.picks[position - 1] : null;
    const isFilled = Boolean(pick?.hero);
    const isBanBox = banPositions.includes(position);
    // Selection/focus state is slot-specific: only active when BOTH map and slot match
    const isThisBoardActive = mapName === activeMap && slotIdx === activeSlotIndex;
    const isSelected = isThisBoardActive && activeScenarioIndex === scenarioIndex && selectedBox === position;
    const isActive = isSelected && showPopup;
    const isMultiSelected = selectedPositions.has(`${mapName}_${scenarioId}_${position}`);
    const isFocused = isThisBoardActive && focusedBox.scenarioIndex === scenarioIndex && focusedBox.position === position;

    const BoxComponent = isBanBox ? BanBox : PickBox;

    const isLargeBox = DraftState.getLargeBoxSequences().includes(position.toString());
    const imageSize = isLargeBox ? 'large' : 'medium';

    return (
      <BoxComponent
        key={`${scenarioId}_${position}`}
        $position={position}
        $team={team}
        $isSimulatorView={isSimulatorView}
        $sizeOverride={sizeOverride}
        $widthOverride={widthOverride}
        data-position={position}
        data-team={team}
        data-box-key={`${mapName}_${scenarioId}_${position}`}
        data-slot-idx={slotIdx}
        data-scenario-id={scenarioId}
        data-connector-target="true"
        tabIndex={isSimulatorView ? -1 : 0}
        className={`${isFilled ? 'filled' : ''} ${!isSimulatorView && isActive ? 'active' : ''} ${!isSimulatorView && isMultiSelected && showFocusIndicator ? 'multi-selected' : ''} ${!isSimulatorView && isFocused && showFocusIndicator ? 'focused' : ''}`}
        onClick={isSimulatorView ? undefined : (e) => {
          setActiveMap(mapName);
          setActiveSlotIndex(slotIdx);
          setSelectedMap(mapName);
          if (handleCmdClick(e, scenarioId, position, scenarioIndex, mapName)) return;
          setHeroSearch('');
          if (searchInputRef.current) searchInputRef.current.value = '';
          handleBoxClick(scenarioIndex, position);
        }}
        onContextMenu={isSimulatorView ? undefined : (e) => handleContextMenu(e, scenarioId, position, mapName)}
        onFocus={isSimulatorView ? undefined : () => {
          setActiveMap(mapName);
          setActiveSlotIndex(slotIdx);
          setSelectedMap(mapName);
          setFocusedBox({ scenarioIndex, position });
        }}
        onTouchStart={isSimulatorView ? undefined : (e) => handleBoxTouchStart(e, scenarioId, position, isFilled, mapName)}
        onTouchMove={isSimulatorView ? undefined : handleBoxTouchMove}
        onTouchEnd={isSimulatorView ? undefined : cancelLongPress}
        onTouchCancel={isSimulatorView ? undefined : cancelLongPress}
        ref={el => { boxRefs.current[`${scenarioId}_${position}`] = el; }}
        style={{
          ...(!isSimulatorView && isActive && {
            boxShadow: `0 0 12px 2px ${isRadiant ? 'rgba(146, 255, 146, 0.7)' : 'rgba(255, 107, 107, 0.7)'}`
          }),
          ...(!isSimulatorView && isFocused && !isActive && {
            outline: `2px solid ${isRadiant ? 'rgba(146, 255, 146, 0.9)' : 'rgba(255, 107, 107, 0.9)'}`,
            outlineOffset: '2px'
          })
        }}
      >
        <PositionLabel $position={position} $compact={!!sizeOverride}>{getDisplayPosition(position)}</PositionLabel>
        <HeroContainer>
          {pick?.hero && (
            <HeroImage
              key={`${pick.hero.id}_${position}_${scenarioId}`}
              hero={pick.hero}
              size={imageSize}
              $grayscale={false}
            />
          )}
        </HeroContainer>
        {pick?.isBanned && <BanIndicator>X</BanIndicator>}
        {isBanBox && <BanCross>✕</BanCross>}
        {/* Counter-relation connector handles on all 4 sides */}
        {isFilled && !isReadOnly && (() => {
          const isDragSource = connectorDrag && connectorDrag.scenarioId === scenarioId && connectorDrag.fromPosition === position;
          const cls = isDragSource ? 'dragging' : '';
          const onDown = (e) => handleConnectorHandleMouseDown(e, scenarioId, position, mapName);
          return (
            <>
              <ConnectorHandle className={`top ${cls}`}    onMouseDown={onDown} title="Drag to mark counter target" />
              <ConnectorHandle className={`right ${cls}`}  onMouseDown={onDown} title="Drag to mark counter target" />
              <ConnectorHandle className={`bottom ${cls}`} onMouseDown={onDown} title="Drag to mark counter target" />
              <ConnectorHandle className={`left ${cls}`}   onMouseDown={onDown} title="Drag to mark counter target" />
            </>
          );
        })()}
      </BoxComponent>
    );
  }, [mapDrafts, pickMode, activeMap, activeSlotIndex, activeScenarioIndex, selectedBox, showPopup, handleBoxClick, boxRefs, selectedPositions, focusedBox, isSimulatorView, connectorDrag, isReadOnly, handleConnectorHandleMouseDown, handleBoxTouchStart, handleBoxTouchMove, cancelLongPress]);

  return (
    <MainContainer
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      {/* Info inputs - hide in simulator/marketing/view-only/embed mode */}
      {!isSimulatorView && !isMarketingDemo && !isEmbedView && !(isSharedView && sharedPermission === 'view') && (
        <InfoInputRow>
          <InfoInput
            placeholder="Team Name"
            value={teamName}
            onChange={e => {
              setTeamName(e.target.value);
              setHasUnsavedChanges && setHasUnsavedChanges(true);
            }}
          />
          <InfoInput
            placeholder="Tournament Name"
            value={tournament}
            onChange={(e) => {
              setTournament(e.target.value);
              setHasUnsavedChanges && setHasUnsavedChanges(true);
            }}
          />
          <InfoInput
            type="text"
            value={stage}
            onChange={(e) => {
              setStage(e.target.value);
              setHasUnsavedChanges && setHasUnsavedChanges(true);
            }}
            placeholder="Stage Name"
          />
          <InfoDateInput
            value={draftDate}
            onChange={e => {
              setDraftDate(e.target.value);
              setHasUnsavedChanges && setHasUnsavedChanges(true);
            }}
          />
        </InfoInputRow>
      )}

      {/* Button row - hide in simulator view */}
      {!isSimulatorView && (
        <TopButtonRow>
          {/* Undo / Redo — icon only, always visible */}
          <AddScenarioButton onClick={undo} title="Undo (Cmd+Z)" style={{ background: '#555', backgroundImage: 'none', minWidth: 'unset', padding: '0 18px' }}>↩</AddScenarioButton>
          <AddScenarioButton onClick={redo} title="Redo (Cmd+Y)" style={{ background: '#555', backgroundImage: 'none', minWidth: 'unset', padding: '0 18px' }}>↪</AddScenarioButton>

          {/* 1st/2nd Pick toggle */}
          <ToggleSwitchContainer>
            <ToggleSwitch>
              <ToggleOption
                $active={pickMode === 'first'}
                onClick={() => setPickMode('first')}
              >
                1st Pick
              </ToggleOption>
              <ToggleOption
                $active={pickMode === 'second'}
                onClick={() => setPickMode('second')}
              >
                2nd Pick
              </ToggleOption>
            </ToggleSwitch>
          </ToggleSwitchContainer>

          {/* Duplicate toggle - only for premium users */}
          {!isMarketingDemo && !(isSharedView && sharedPermission === 'view') && (
            <>
              {UserTierService.hasFeatureAccess(userProfile, 'canSeeDuplicateToggle') && (
                <ToggleSwitchContainer>
                  <ToggleSwitch style={{ minWidth: '140px', border: `2px solid ${preventDuplicates ? '#f44336' : '#666'}` }}>
                    <ToggleOption
                      $active={preventDuplicates}
                      onClick={() => setPreventDuplicates(true)}
                      style={{
                        background: preventDuplicates ? '#f44336' : 'transparent',
                        color: preventDuplicates ? '#fff' : '#aaa'
                      }}
                    >
                      Duplicate ✕
                    </ToggleOption>
                    <ToggleOption
                      $active={!preventDuplicates}
                      onClick={() => setPreventDuplicates(false)}
                      style={{
                        background: !preventDuplicates ? '#f44336' : 'transparent',
                        color: !preventDuplicates ? '#fff' : '#aaa'
                      }}
                    >
                      Duplicate ✓
                    </ToggleOption>
                  </ToggleSwitch>
                </ToggleSwitchContainer>
              )}
            </>
          )}

          {/* Only show these buttons if NOT in View Only shared mode */}
          {!(isSharedView && sharedPermission === 'view') && (
            <>
              {/* Add Scenario button */}
              {(!showButtons || showButtons.addScenario) && !(maxScenariosDemo && scenarios.length >= maxScenariosDemo) && (
                <AddScenarioButton onClick={handleAddScenario}>
                  <span style={{ fontSize: '18px', marginRight: '5px' }}>+</span> Add Scenario
                </AddScenarioButton>
              )}
              {!showButtons && (
                <>
                  {/* Copy From button - premium only */}
                  {UserTierService.hasFeatureAccess(userProfile, 'canSeeCopyFromPickMode') && (
                    <AddScenarioButton onClick={handleCopyFromClick} style={{ background: '#4caf50', borderColor: '#4caf50' }}>
                      <span style={{ fontSize: '16px', marginRight: '4px' }}>📑</span> Copy &gt; {pickMode === 'first' ? '2nd' : '1st'}
                    </AddScenarioButton>
                  )}
                  {UserTierService.hasFeatureAccess(userProfile, 'canSeeCopyFromPickMode') && (
                    <AddScenarioButton onClick={() => setShowBothOverlay(true)} style={{ background: '#7b1fa2', borderColor: '#7b1fa2' }}>
                      <span style={{ fontSize: '16px', marginRight: '4px' }}>👁</span> Show Both
                    </AddScenarioButton>
                  )}
                  {!isEmbedView && (
                    <NewDraftButton onClick={handleNewDraft}>
                      <span style={{ fontSize: '16px', marginRight: '4px' }}>📄</span> New Draft
                    </NewDraftButton>
                  )}
                  {/* VIEW RECORDS BUTTON — commented out intentionally, not ready for use yet.
                      When re-enabling: this button is premium-tier gated (canSeeViewRecords in
                      UserTier.js) and navigates to /records (RecordViewer page). It requires
                      Supabase save to be active first so drafts are actually stored there.
                      To re-enable, uncomment the block below AND uncomment the Supabase save
                      block in DraftContext.js saveDraft(). */}
                  {/* {UserTierService.hasFeatureAccess(userProfile, 'canSeeViewRecords') && (
                    <RecordViewerButton onClick={() => navigate('/records')}>
                      <span style={{ fontSize: '18px', marginRight: '5px' }}>📁</span> View Records
                    </RecordViewerButton>
                  )} */}
                  {/* Toggle between all maps and General-only */}
                  {boardMaps.length > 1 ? (
                    <ClearMapsButton onClick={handleClearNonGeneralMaps}>
                      🗑 Clear Maps
                    </ClearMapsButton>
                  ) : (
                    <AddScenarioButton onClick={handleAddAllMaps} style={{ background: '#7B1FA2', backgroundImage: 'linear-gradient(to bottom, #9C27B0, #7B1FA2)' }}>
                      🗺 Add Maps
                    </AddScenarioButton>
                  )}
                </>
              )}
            </>
          )}

          {/* Save Draft button — hidden in embed mode (cloud save is host-app's
              concern; the embed only edits within-session via sessionStorage). */}
          {!showButtons && !isEmbedView && (
            <AddScenarioButton
              onClick={handleSaveDraft}
              style={{
                backgroundColor: '#4CAF50',
                backgroundImage: 'linear-gradient(to bottom, #4CAF50, #388E3C)',
              }}
            >
              Save Draft
            </AddScenarioButton>
          )}
        </TopButtonRow>
      )}

      {/* Desktop view toggle: Normal | Drafter POV — hidden on mobile, shown below main buttons */}
      {!isSimulatorView && !isMarketingDemo && (
        <DesktopOnlyToggleContainer style={{ justifyContent: 'center', width: '100%', marginBottom: '8px' }}>
          <ToggleSwitchContainer>
            <ToggleSwitch style={{ border: '2px solid #2196F3' }}>
              <ToggleOption
                $active={desktopView === 'normal'}
                onClick={() => setDesktopView('normal')}
                style={{
                  background: desktopView === 'normal' ? '#2196F3' : 'transparent',
                  color: desktopView === 'normal' ? '#fff' : '#aaa',
                }}
              >
                Normal
              </ToggleOption>
              <ToggleOption
                $active={desktopView === 'drafter'}
                onClick={() => setDesktopView('drafter')}
                style={{
                  background: desktopView === 'drafter' ? '#2196F3' : 'transparent',
                  color: desktopView === 'drafter' ? '#fff' : '#aaa',
                }}
              >
                Drafter POV
              </ToggleOption>
            </ToggleSwitch>
          </ToggleSwitchContainer>
        </DesktopOnlyToggleContainer>
      )}

      {/* 15 forehead hero slots — between button row and draft boards */}
      {!isSimulatorView && !isMarketingDemo && !isEmbedView && !(isSharedView && sharedPermission === 'view') && (
        <ForeheadRow ref={foreheadRowRef}>
          {foreheadPicks.map((pick, idx) => {
            const isSelectedHere = isForeheadMode && showPopup && selectedBox === idx + 1;
            const manuallyCrossed = manualCrosses.has(idx);
            const isCrossed = manuallyCrossed;
            const isForeheadSelected = foreheadSelection.has(idx);
            const isFilled = !!pick?.hero;
            const isDragSource = foreheadConnectorDrag && foreheadConnectorDrag.fromIdx === idx;
            const isNeonTarget = foreheadConnectors.some(c => {
              if (c.to !== idx) return false;
              return manualCrosses.has(c.from);
            });
            return (
              <PickBox
                key={`forehead-${idx}`}
                ref={el => foreheadBoxRefs.current[idx] = el}
                $position={'8'}
                $team={'Radiant'}
                data-forehead-target="true"
                data-forehead-idx={idx}
                className={`${isFilled ? 'filled' : ''} ${isSelectedHere ? 'selected' : ''} ${isForeheadSelected ? 'multi-selected' : ''}`}
                onClick={(e) => handleForeheadBoxClick(idx, e)}
                onContextMenu={(e) => handleForeheadContextMenu(e, idx)}
                title={pick?.hero ? `${pick.hero.name}${isCrossed ? ' — crossed' : ''} (Cmd/Alt+click to toggle cross)` : `Slot ${idx + 1} (Cmd/Alt+click to toggle cross)`}
                style={{ position: 'relative', ...(isNeonTarget && { boxShadow: '0 0 8px 3px #00ff88, 0 0 18px 5px rgba(0,255,136,0.5)', border: '2px solid #00ff88' }) }}
              >
                {pick?.hero && (
                  <HeroContainer>
                    <HeroImage hero={pick.hero} $grayscale={isCrossed} />
                  </HeroContainer>
                )}
                {isCrossed && <BanCrossOverlay />}
                {isFilled && !isReadOnly && (
                  <ConnectorHandle
                    className={`top ${isDragSource ? 'dragging' : ''}`}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setForeheadConnectorDrag({ fromIdx: idx, mouseX: e.clientX, mouseY: e.clientY });
                    }}
                    title="Drag to draw relation arrow"
                  />
                )}
              </PickBox>
            );
          })}
          <button
            type="button"
            onClick={clearForeheadCrosses}
            title="Clear all crosses on the 15 slots"
            style={{ marginLeft: '8px', padding: '6px 12px', background: '#333', color: '#fff', border: '1px solid #555', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
          >
            ↻ Refresh
          </button>
          {foreheadConnectors.length > 0 && (
            <button
              type="button"
              onClick={() => setForeheadConnectors([])}
              style={{ marginLeft: '4px', padding: '6px 12px', background: '#1b5e20', color: '#fff', border: '1px solid #2e7d32', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}
            >
              Clear Arrows
            </button>
          )}
          {/* SVG overlay for forehead connector arrows — fixed viewport coords, arcs above boxes */}
          {(() => {
            const arcLift = 70;
            const getTopCenter = (i) => {
              const el = foreheadBoxRefs.current[i];
              if (!el) return null;
              const r = el.getBoundingClientRect();
              return { x: r.left + r.width / 2, y: r.top };
            };
            const markerId = 'fh-arrow';
            const hasContent = foreheadConnectors.length > 0 || !!foreheadConnectorDrag;
            if (!hasContent) return null;
            let dragPath = null;
            if (foreheadConnectorDrag) {
              const from = getTopCenter(foreheadConnectorDrag.fromIdx);
              if (from) {
                const tx = foreheadConnectorDrag.mouseX;
                const ty = foreheadConnectorDrag.mouseY;
                const mx = (from.x + tx) / 2;
                const cy = Math.min(from.y, ty) - arcLift;
                dragPath = `M ${from.x} ${from.y} Q ${mx} ${cy} ${tx} ${ty}`;
              }
            }
            return (
              <svg style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 200, overflow: 'visible' }}>
                <defs>
                  <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#00ff88" />
                  </marker>
                </defs>
                {foreheadConnectors.map((c, i) => {
                  const a = getTopCenter(c.from);
                  const b = getTopCenter(c.to);
                  if (!a || !b) return null;
                  const mx = (a.x + b.x) / 2;
                  const cy = Math.min(a.y, b.y) - arcLift;
                  return (
                    <React.Fragment key={i}>
                      <path
                        d={`M ${a.x} ${a.y} Q ${mx} ${cy} ${b.x} ${b.y}`}
                        fill="none"
                        stroke="rgba(0,255,136,0.85)"
                        strokeWidth="3"
                        strokeDasharray="8 6"
                        strokeLinecap="round"
                        markerEnd={`url(#${markerId})`}
                        style={{ pointerEvents: 'none' }}
                      />
                      <path
                        d={`M ${a.x} ${a.y} Q ${mx} ${cy} ${b.x} ${b.y}`}
                        fill="none"
                        stroke="transparent"
                        strokeWidth="20"
                        style={{ cursor: 'context-menu', pointerEvents: 'stroke' }}
                        onContextMenu={(e) => { e.preventDefault(); setForeheadConnectors(prev => prev.filter((_, j) => j !== i)); }}
                      />
                    </React.Fragment>
                  );
                })}
                {dragPath && (
                  <path
                    d={dragPath}
                    fill="none"
                    stroke="#00b4d8"
                    strokeWidth="3"
                    strokeDasharray="5 4"
                    strokeLinecap="round"
                  />
                )}
              </svg>
            );
          })()}
        </ForeheadRow>
      )}

      {/* All maps visible - side by side, each slot shows all its scenarios */}
      {/* Desktop Drafter POV view */}
      {desktopView === 'drafter' && (
        <DesktopDrafterContainer>
          {boardMaps.flatMap((mapName, slotIndex) => {
            const slotLabel = boardLabels[slotIndex];
            const mapBorderColor = MAP_BORDER_COLORS[slotLabel] || MAP_BORDER_COLORS['General'];
            const mapScenarios = pickMode === 'first'
              ? (mapDrafts[mapName]?.scenariosFirstPick || [])
              : (mapDrafts[mapName]?.scenariosSecondPick || []);

            return mapScenarios.map((scenario, sIdx) => {
              const borderColorStr = `rgba(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b}, 0.6)`;
              const swapped = scenario.swapSides || false;
              const usBans    = swapped ? [2, 3, 5, 26, 21]    : [1, 4, 7, 27, 22];
              const enemyBans = swapped ? [1, 4, 7, 27, 22]    : [2, 3, 5, 26, 21];
              const usPicks   = swapped ? [13, 16, 17, 24, 25] : [14, 15, 18, 23, 20];
              const enemyPicks= swapped ? [14, 15, 18, 23, 20] : [13, 16, 17, 24, 25];
              const usTeam    = swapped ? 'Dire' : 'Radiant';
              const enemyTeam = swapped ? 'Radiant' : 'Dire';

              return (
                <DesktopDrafterCard key={`desktop_drafter_${mapName}_${scenario.id}`} $borderColor={borderColorStr} $pickMode={pickMode} $swapped={swapped}>
                  {/* Header */}
                  <MobileBoardHeader>
                    <MapDropdown
                      value={slotLabel}
                      style={{
                        fontSize: '10px', padding: '2px 18px 2px 6px',
                        color: `rgb(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b})`,
                        borderColor: `rgba(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b}, 0.5)`,
                        background: `rgba(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b}, 0.15)`,
                        backgroundColor: `rgba(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b}, 0.15)`,
                      }}
                      onChange={(e) => setBoardLabels(prev => prev.map((l, i) => i === slotIndex ? e.target.value : l))}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {MAP_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </MapDropdown>
                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {UserTierService.hasFeatureAccess(userProfile, 'canDuplicate') && (
                        <DuplicateScenarioButton onClick={() => duplicateScenario(pickMode, scenario.id, mapName)} style={{ minWidth: 'unset', padding: '4px 8px', fontSize: '12px', height: 'auto' }}>📋 Dup</DuplicateScenarioButton>
                      )}
                      {UserTierService.hasFeatureAccess(userProfile, 'canSeeCopyToPickMode') && (
                        <CopyScenarioButton onClick={() => handleCopyToOtherMode(scenario, pickMode, mapName)} style={{ minWidth: 'unset', padding: '4px 8px', fontSize: '12px', height: 'auto' }}>📑 Copy→{pickMode === 'first' ? '2nd' : '1st'}</CopyScenarioButton>
                      )}
                      <DeleteScenarioButton onClick={() => deleteScenario(pickMode, scenario.id, mapName)} style={{ minWidth: 'unset', padding: '4px 8px', fontSize: '12px', height: 'auto' }}>🗑</DeleteScenarioButton>
                      <ClearButton onClick={() => updateScenario(pickMode, scenario.id, { picks: Array(24).fill(null) }, mapName)} style={{ padding: '4px 8px', fontSize: '12px', minWidth: 'unset' }}>Clear</ClearButton>
                      {Array.isArray(scenario.counterConnectors) && scenario.counterConnectors.length > 0 && (
                        <ClearButton onClick={() => updateScenario(pickMode, scenario.id, { counterConnectors: [] }, mapName)} style={{ padding: '4px 8px', fontSize: '12px', minWidth: 'unset', background: '#1b5e20', border: '1px solid #2e7d32' }} title="Remove all counter-relation arrows">Clear Relations</ClearButton>
                      )}
                      <ClearButton onClick={() => handleSwapSides(scenario, mapName)} style={{ padding: '4px 8px', fontSize: '13px', minWidth: 'unset', background: swapped ? '#2196F3' : '#333', border: swapped ? '1px solid #2196F3' : '1px solid #555' }}>⇄</ClearButton>
                    </div>
                  </MobileBoardHeader>

                  {/* Connector overlay wraps bans + picks so arrows can span the full card */}
                  <div style={{ position: 'relative' }} ref={el => { picksContainerRefs.current[`drafter_d_${mapName}_${scenario.id}`] = el; }}>
                    {/* Bans row */}
                    <MobileBansSection style={{ marginTop: '8px' }}>
                      <MobileBansGroup>{usBans.map(pos => renderPickBox(sIdx, pos, mapName, slotIndex, '64px'))}</MobileBansGroup>
                      <MobileBansGroup>{enemyBans.map(pos => renderPickBox(sIdx, pos, mapName, slotIndex, '64px'))}</MobileBansGroup>
                    </MobileBansSection>

                    {/* Team labels */}
                    <MobileTeamsLabel>
                      <TeamHeader $team={usTeam} style={{ fontSize: '15px', width: 'auto', padding: '0 30px' }}>Us</TeamHeader>
                      <TeamHeader $team={enemyTeam} style={{ fontSize: '15px', width: 'auto', padding: '0 30px' }}>Enemy</TeamHeader>
                    </MobileTeamsLabel>

                    {/* Picks: 2 vertical columns */}
                    <MobilePicksSection style={{ gap: '20px' }}>
                      <MobilePickColumn style={{ gap: '8px' }}>{usPicks.map(pos => renderPickBox(sIdx, pos, mapName, slotIndex, '96px'))}</MobilePickColumn>
                      <MobilePickColumn style={{ gap: '8px' }}>{enemyPicks.map(pos => renderPickBox(sIdx, pos, mapName, slotIndex, '96px'))}</MobilePickColumn>
                    </MobilePicksSection>

                    {/* Counter-relation connector overlay */}
                    {(() => {
                      void connectorTick;
                      const refKey = `drafter_d_${mapName}_${scenario.id}`;
                      const container = picksContainerRefs.current[refKey];
                      if (!container) return null;
                      const cRect = container.getBoundingClientRect();
                      const vbW = Math.max(1, cRect.width);
                      const vbH = Math.max(1, cRect.height);
                      const getCenter = (pos) => {
                        const el = container.querySelector(`[data-scenario-id="${scenario.id}"][data-position="${pos}"][data-connector-target="true"]`);
                        if (!el) return null;
                        const r = el.getBoundingClientRect();
                        return { x: r.left - cRect.left + r.width / 2, y: r.top - cRect.top + r.height / 2 };
                      };
                      const connectors = Array.isArray(scenario.counterConnectors) ? scenario.counterConnectors : [];
                      const markerId = `cc-arrow-dd-${mapName}-${scenario.id}`;
                      const isDraggingHere = connectorDrag && connectorDrag.scenarioId === scenario.id && connectorDrag.mapName === mapName;
                      let dragLine = null;
                      if (isDraggingHere) {
                        const from = getCenter(connectorDrag.fromPosition);
                        if (from) dragLine = { x1: from.x, y1: from.y, x2: connectorDrag.mouseX - cRect.left, y2: connectorDrag.mouseY - cRect.top };
                      }
                      return (
                        <ConnectorSvg viewBox={`0 0 ${vbW} ${vbH}`} preserveAspectRatio="none">
                          <defs>
                            <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                              <path d="M 0 0 L 10 5 L 0 10 z" fill="#00ff88" />
                            </marker>
                          </defs>
                          {connectors.map((c, i) => {
                            const a = getCenter(c.from);
                            const b = getCenter(c.to);
                            if (!a || !b) return null;
                            return (
                              <path key={`${c.from}-${c.to}-${i}`} className="connector-path" d={`M ${a.x} ${a.y} L ${b.x} ${b.y}`} markerEnd={`url(#${markerId})`}
                                onClick={(e) => { e.stopPropagation(); if (!isReadOnly) removeCounterConnector(mapName, scenario.id, i); }} />
                            );
                          })}
                          {dragLine && <path className="connector-path drag-preview" d={`M ${dragLine.x1} ${dragLine.y1} L ${dragLine.x2} ${dragLine.y2}`} />}
                        </ConnectorSvg>
                      );
                    })()}
                  </div>
                </DesktopDrafterCard>
              );
            });
          })}
        </DesktopDrafterContainer>
      )}

      {desktopView === 'normal' && <AllMapsContainer>
        {boardMaps.map((mapName, slotIndex) => {
          const slotLabel = boardLabels[slotIndex];
          const mapBorderColor = MAP_BORDER_COLORS[slotLabel] || MAP_BORDER_COLORS['General'];
          const mapFirstPick = mapDrafts[mapName]?.scenariosFirstPick || [];
          const mapSecondPick = mapDrafts[mapName]?.scenariosSecondPick || [];
          const mapScenariosList = pickMode === 'first' ? mapFirstPick : mapSecondPick;
          const isSlotActive = mapName === activeMap && slotIndex === activeSlotIndex;

          return (
            <MapSectionWrapper key={mapName} $mapColor={mapBorderColor} data-slot-idx={slotIndex} data-map-name={mapName}>
              {/* Map dropdown in column header */}
              <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 4px 2px' }}>
                <MapDropdown
                  value={slotLabel}
                  style={{
                    color: `rgb(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b})`,
                    borderColor: `rgba(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b}, 0.5)`,
                    background: `rgba(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b}, 0.15)`,
                    backgroundColor: `rgba(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b}, 0.15)`,
                  }}
                  onChange={(e) => {
                    setBoardLabels(prev => prev.map((l, i) => i === slotIndex ? e.target.value : l));
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  {MAP_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </MapDropdown>
              </div>
              <ScenarioBoardsRow
                ref={isSlotActive ? (el) => {
                  scrollContainerRef.current = el;
                  draftContentRef.current = el;
                } : undefined}
              >
              {mapScenariosList.map((scenario, scenarioIndex) => {
                  const isThisMapActive = isSlotActive;

                  return (
                    <DraftBoardContainer
                        key={scenario.id}
                        className="draft-board"
                        ref={el => { scenarioRefs.current[`${mapName}_${scenario.id}`] = el; }}
                        $swapSides={scenario.swapSides || false}
                        onDragOver={(e) => handleScenarioDragOver(e, scenario.id, mapName)}
                        onDragLeave={(e) => handleScenarioDragLeave(e, scenario.id, mapName)}
                        $isDragging={draggedScenarioId === scenario.id && draggedScenarioMap === mapName}
                        $isOver={dragOverScenarioId && typeof dragOverScenarioId === 'object' ? dragOverScenarioId.id === scenario.id && dragOverScenarioId.mapName === mapName : false}
                        $justDropped={droppedScenarioId === `${mapName}_${scenario.id}`}
                        $fromMatchPull={scenario.fromMatchPull}
                        $matchColorIndex={scenario.matchColorIndex}
                      >

                        {/* Drag handle - always visible, premium only */}
                        {UserTierService.hasFeatureAccess(userProfile, 'canSeeSorting') && (
                          <DragHandle
                            onDragStart={(e) => handleScenarioDragStart(e, scenario.id, mapName)}
                            onDragEnd={(e) => handleScenarioDragEnd(e)}
                          />
                        )}

                        <ScenarioInput
                          type="text"
                          placeholder="Enter scenario name..."
                          value={scenario.title || ''}
                          onChange={(e) => updateScenario(pickMode, scenario.id, { title: e.target.value }, mapName)}
                          onFocus={() => {
                            setActiveMap(mapName);
                            setActiveSlotIndex(slotIndex);
                            setSelectedMap(mapName);
                            setActiveScenarioIndex(scenarioIndex);
                          }}
                        />

                        <ScenarioTitle>
                          {pickMode === 'first' ?
                            <span style={{ color: '#1de9b6' }}>1st Pick</span> :
                            <span style={{ color: '#61dafb' }}>2nd Pick</span>} Scenario {scenarioIndex + 1}
                        </ScenarioTitle>

                        {/* Scenario action buttons - hide in simulator view */}
                        {!isSimulatorView && (
                          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '6px', gap: '4px', flexWrap: 'wrap' }}>
                            {/* Duplicate button */}
                            {(!showButtons || showButtons.duplicate) && UserTierService.hasFeatureAccess(userProfile, 'canDuplicate') && (
                              <DuplicateScenarioButton onClick={() => duplicateScenario(pickMode, scenario.id, mapName)} style={{ minWidth: 'unset', padding: '3px 6px', fontSize: '10px' }}>
                                📋 Dup
                              </DuplicateScenarioButton>
                            )}
                            {/* Copy to other mode - premium only */}
                            {!showButtons && UserTierService.hasFeatureAccess(userProfile, 'canSeeCopyToPickMode') && (
                              <CopyScenarioButton onClick={() => handleCopyToOtherMode(scenario, pickMode, mapName)} style={{ minWidth: 'unset', padding: '3px 6px', fontSize: '10px' }}>
                                📑 Copy→{pickMode === 'first' ? '2nd' : '1st'}
                              </CopyScenarioButton>
                            )}
                            {/* Delete button - shows whenever more than 1 draftboard exists on screen */}
                            {(!showButtons || showButtons.delete) && (boardMaps.length > 1 || mapScenariosList.length > 1) && (
                              <DeleteScenarioButton
                                onClick={() => {
                                  setBoardMaps(prev => prev.filter((_, i) => i !== slotIndex));
                                  setBoardLabels(prev => prev.filter((_, i) => i !== slotIndex));
                                }}
                                style={{ minWidth: 'unset', padding: '3px 6px', fontSize: '10px' }}
                              >
                                🗑
                              </DeleteScenarioButton>
                            )}
                            {/* Clear All button */}
                            {(!showButtons || showButtons.clearAll) && (
                              <ClearButton
                                onClick={() => updateScenario(pickMode, scenario.id, { picks: Array(24).fill(null) }, mapName)}
                                style={{ padding: '3px 6px', fontSize: '10px', minWidth: 'unset', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              >
                                Clear
                              </ClearButton>
                            )}
                            {/* Clear Relations - removes all counter-relation arrows for this scenario */}
                            {!showButtons && Array.isArray(scenario.counterConnectors) && scenario.counterConnectors.length > 0 && (
                              <ClearButton
                                onClick={() => updateScenario(pickMode, scenario.id, { counterConnectors: [] }, mapName)}
                                style={{ padding: '3px 6px', fontSize: '10px', minWidth: 'unset', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1b5e20', border: '1px solid #2e7d32' }}
                                title="Remove all counter-relation arrows from this scenario"
                              >
                                Clear Relations
                              </ClearButton>
                            )}
                            {/* Swap Sides button */}
                            <ClearButton
                              onClick={() => handleSwapSides(scenario, mapName)}
                              style={{
                                padding: '3px 6px',
                                fontSize: '13px',
                                minWidth: 'unset',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: (scenario.swapSides || false) ? '#2196F3' : '#333',
                                border: (scenario.swapSides || false) ? '1px solid #2196F3' : '1px solid #555'
                              }}
                            >
                              {(scenario.swapSides || false) ? '↔️' : '⇄'}
                            </ClearButton>
                          </div>
                        )}

                        <TeamsContainer>
                          {(() => {
                            const swapped = scenario.swapSides || false;
                            const leftLabel = "Us";
                            const rightLabel = "Enemy";
                            const leftTeam = swapped ? "Dire" : "Radiant";
                            const rightTeam = swapped ? "Radiant" : "Dire";

                            if (isMarketingDemo) {
                              return (
                                <>
                                  <TeamHeader $team={leftTeam} style={{ fontSize: '14px' }}>My Team</TeamHeader>
                                  <TeamHeader $team={rightTeam} style={{ fontSize: '14px' }}>Opponent</TeamHeader>
                                </>
                              );
                            } else {
                              return (
                                <>
                                  <TeamHeader $team={leftTeam}>{leftLabel}</TeamHeader>
                                  <TeamHeader $team={rightTeam}>{rightLabel}</TeamHeader>
                                </>
                              );
                            }
                          })()}
                        </TeamsContainer>

                        {/* MLBB board layout - all maps fully interactive */}
                        <PicksContainer ref={el => { picksContainerRefs.current[`${mapName}_${scenario.id}`] = el; }}>
                          {!(scenario.swapSides || false) ? (
                            <>
                              <TeamColumn>
                                {renderPickBox(scenarioIndex, 1, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 4, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 7, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 14, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 15, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 18, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 27, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 22, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 23, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 20, mapName, slotIndex)}
                              </TeamColumn>
                              <TeamColumn>
                                {renderPickBox(scenarioIndex, 2, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 3, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 5, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 13, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 16, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 17, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 26, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 21, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 24, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 25, mapName, slotIndex)}
                              </TeamColumn>
                            </>
                          ) : (
                            <>
                              <TeamColumn>
                                {renderPickBox(scenarioIndex, 2, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 3, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 5, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 13, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 16, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 17, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 26, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 21, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 24, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 25, mapName, slotIndex)}
                              </TeamColumn>
                              <TeamColumn>
                                {renderPickBox(scenarioIndex, 1, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 4, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 7, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 14, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 15, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 18, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 27, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 22, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 23, mapName, slotIndex)}
                                {renderPickBox(scenarioIndex, 20, mapName, slotIndex)}
                              </TeamColumn>
                            </>
                          )}
                          {/* Counter-relation connector overlay */}
                          {(() => {
                            void connectorTick;
                            const container = picksContainerRefs.current[`${mapName}_${scenario.id}`];
                            if (!container) return null;
                            const cRect = container.getBoundingClientRect();
                            const vbW = Math.max(1, cRect.width);
                            const vbH = Math.max(1, cRect.height);
                            // Query directly inside this PicksContainer to avoid using a stale
                            // boxRef that was overwritten by a different render view (e.g. mobile
                            // drafter section renders the same scenarioId+position with its own ref).
                            const getCenter = (pos) => {
                              const el = container.querySelector(`[data-scenario-id="${scenario.id}"][data-position="${pos}"][data-connector-target="true"]`);
                              if (!el) return null;
                              const r = el.getBoundingClientRect();
                              return {
                                x: r.left - cRect.left + r.width / 2,
                                y: r.top - cRect.top + r.height / 2,
                              };
                            };
                            const connectors = Array.isArray(scenario.counterConnectors) ? scenario.counterConnectors : [];
                            const markerId = `cc-arrow-${mapName}-${scenario.id}`;
                            const isDraggingHere = connectorDrag && connectorDrag.scenarioId === scenario.id && connectorDrag.mapName === mapName;
                            let dragLine = null;
                            if (isDraggingHere) {
                              const from = getCenter(connectorDrag.fromPosition);
                              if (from) {
                                dragLine = {
                                  x1: from.x,
                                  y1: from.y,
                                  x2: connectorDrag.mouseX - cRect.left,
                                  y2: connectorDrag.mouseY - cRect.top,
                                };
                              }
                            }
                            return (
                              <ConnectorSvg viewBox={`0 0 ${vbW} ${vbH}`} preserveAspectRatio="none">
                                <defs>
                                  <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#00ff88" />
                                  </marker>
                                </defs>
                                {connectors.map((c, i) => {
                                  const a = getCenter(c.from);
                                  const b = getCenter(c.to);
                                  if (!a || !b) return null;
                                  return (
                                    <path
                                      key={`${c.from}-${c.to}-${i}`}
                                      className="connector-path"
                                      d={`M ${a.x} ${a.y} L ${b.x} ${b.y}`}
                                      markerEnd={`url(#${markerId})`}
                                      onClick={(e) => { e.stopPropagation(); if (!isReadOnly) removeCounterConnector(mapName, scenario.id, i); }}
                                    />
                                  );
                                })}
                                {dragLine && (
                                  <path
                                    className="connector-path drag-preview"
                                    d={`M ${dragLine.x1} ${dragLine.y1} L ${dragLine.x2} ${dragLine.y2}`}
                                  />
                                )}
                              </ConnectorSvg>
                            );
                          })()}
                        </PicksContainer>
                    </DraftBoardContainer>
                  );
                })}
              </ScenarioBoardsRow>
            </MapSectionWrapper>
          );
        })}
      </AllMapsContainer>}

      {/* Mobile landscape view toggle — only visible in landscape */}
      <MobileLandscapeToggleBar>
        <MobileLandscapeToggleSwitch>
          <MobileLandscapeToggleOption
            $active={mobileLandscapeView === 'drafter'}
            onClick={() => setMobileLandscapeView('drafter')}
          >
            Drafter POV
          </MobileLandscapeToggleOption>
          <MobileLandscapeToggleOption
            $active={mobileLandscapeView === 'board'}
            onClick={() => setMobileLandscapeView('board')}
          >
            Draft Board View
          </MobileLandscapeToggleOption>
        </MobileLandscapeToggleSwitch>
      </MobileLandscapeToggleBar>

      {/* Drafter POV carousel — bans on top, picks as 2 vertical columns, one card = 50vw */}
      <MobileDrafterOuter $active={mobileLandscapeView === 'drafter'}>
        <MobileCarouselScroll>
          {boardMaps.flatMap((mapName, slotIndex) => {
            const slotLabel = boardLabels[slotIndex];
            const mapBorderColor = MAP_BORDER_COLORS[slotLabel] || MAP_BORDER_COLORS['General'];
            const mapScenarios = pickMode === 'first'
              ? (mapDrafts[mapName]?.scenariosFirstPick || [])
              : (mapDrafts[mapName]?.scenariosSecondPick || []);

            return mapScenarios.map((scenario, sIdx) => {
              const borderColorStr = `rgba(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b}, 0.6)`;
              const swapped = scenario.swapSides || false;
              const usBans    = swapped ? [2, 3, 5, 26, 21]    : [1, 4, 7, 27, 22];
              const enemyBans = swapped ? [1, 4, 7, 27, 22]    : [2, 3, 5, 26, 21];
              const usPicks   = swapped ? [13, 16, 17, 24, 25] : [14, 15, 18, 23, 20];
              const enemyPicks= swapped ? [14, 15, 18, 23, 20] : [13, 16, 17, 24, 25];
              const usTeam    = swapped ? 'Dire' : 'Radiant';
              const enemyTeam = swapped ? 'Radiant' : 'Dire';

              return (
                <MobileDrafterCard key={`drafter_${mapName}_${scenario.id}`} $borderColor={borderColorStr} $pickMode={pickMode} $swapped={swapped}>
                  {/* Header */}
                  <MobileBoardHeader>
                    <MapDropdown
                      value={slotLabel}
                      style={{
                        fontSize: '9px', padding: '2px 18px 2px 6px',
                        color: `rgb(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b})`,
                        borderColor: `rgba(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b}, 0.5)`,
                        background: `rgba(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b}, 0.15)`,
                        backgroundColor: `rgba(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b}, 0.15)`,
                      }}
                      onChange={(e) => setBoardLabels(prev => prev.map((l, i) => i === slotIndex ? e.target.value : l))}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {MAP_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </MapDropdown>
                    <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {UserTierService.hasFeatureAccess(userProfile, 'canDuplicate') && (
                        <DuplicateScenarioButton
                          onClick={() => duplicateScenario(pickMode, scenario.id, mapName)}
                          style={{ minWidth: 'unset', padding: '2px 4px', fontSize: '9px', height: 'auto' }}
                        >📋 Dup</DuplicateScenarioButton>
                      )}
                      {UserTierService.hasFeatureAccess(userProfile, 'canSeeCopyToPickMode') && (
                        <CopyScenarioButton
                          onClick={() => handleCopyToOtherMode(scenario, pickMode, mapName)}
                          style={{ minWidth: 'unset', padding: '2px 4px', fontSize: '9px', height: 'auto' }}
                        >📑 Copy→{pickMode === 'first' ? '2nd' : '1st'}</CopyScenarioButton>
                      )}
                      <DeleteScenarioButton
                        onClick={() => deleteScenario(pickMode, scenario.id, mapName)}
                        style={{ minWidth: 'unset', padding: '2px 4px', fontSize: '9px', height: 'auto' }}
                      >🗑</DeleteScenarioButton>
                      <ClearButton
                        onClick={() => updateScenario(pickMode, scenario.id, { picks: Array(24).fill(null) }, mapName)}
                        style={{ padding: '2px 5px', fontSize: '9px', minWidth: 'unset' }}
                      >Clear</ClearButton>
                      {Array.isArray(scenario.counterConnectors) && scenario.counterConnectors.length > 0 && (
                        <ClearButton onClick={() => updateScenario(pickMode, scenario.id, { counterConnectors: [] }, mapName)} style={{ padding: '2px 5px', fontSize: '9px', minWidth: 'unset', background: '#1b5e20', border: '1px solid #2e7d32' }} title="Remove all counter-relation arrows">Clear Relations</ClearButton>
                      )}
                      <ClearButton
                        onClick={() => handleSwapSides(scenario, mapName)}
                        style={{
                          padding: '2px 5px', fontSize: '11px', minWidth: 'unset',
                          background: swapped ? '#2196F3' : '#333',
                          border: swapped ? '1px solid #2196F3' : '1px solid #555',
                        }}
                      >⇄</ClearButton>
                    </div>
                  </MobileBoardHeader>

                  {/* Connector overlay wraps bans + picks so arrows can span the full card */}
                  <div style={{ position: 'relative' }} ref={el => { picksContainerRefs.current[`drafter_m_${mapName}_${scenario.id}`] = el; }}>
                    {/* Bans row: Us | Enemy */}
                    <MobileBansSection>
                      <MobileBansGroup>
                        {usBans.map(pos => renderPickBox(sIdx, pos, mapName, slotIndex, '32px'))}
                      </MobileBansGroup>
                      <MobileBansGroup>
                        {enemyBans.map(pos => renderPickBox(sIdx, pos, mapName, slotIndex, '32px'))}
                      </MobileBansGroup>
                    </MobileBansSection>

                    {/* Team labels */}
                    <MobileTeamsLabel>
                      <TeamHeader $team={usTeam} style={{ fontSize: '11px', width: 'auto', padding: '0 16px' }}>Us</TeamHeader>
                      <TeamHeader $team={enemyTeam} style={{ fontSize: '11px', width: 'auto', padding: '0 16px' }}>Enemy</TeamHeader>
                    </MobileTeamsLabel>

                    {/* Picks: 2 vertical columns */}
                    <MobilePicksSection>
                      <MobilePickColumn>
                        {usPicks.map(pos => renderPickBox(sIdx, pos, mapName, slotIndex, '42px'))}
                      </MobilePickColumn>
                      <MobilePickColumn>
                        {enemyPicks.map(pos => renderPickBox(sIdx, pos, mapName, slotIndex, '42px'))}
                      </MobilePickColumn>
                    </MobilePicksSection>

                    {/* Counter-relation connector overlay */}
                    {(() => {
                      void connectorTick;
                      const refKey = `drafter_m_${mapName}_${scenario.id}`;
                      const container = picksContainerRefs.current[refKey];
                      if (!container) return null;
                      const cRect = container.getBoundingClientRect();
                      const vbW = Math.max(1, cRect.width);
                      const vbH = Math.max(1, cRect.height);
                      const getCenter = (pos) => {
                        const el = container.querySelector(`[data-scenario-id="${scenario.id}"][data-position="${pos}"][data-connector-target="true"]`);
                        if (!el) return null;
                        const r = el.getBoundingClientRect();
                        return { x: r.left - cRect.left + r.width / 2, y: r.top - cRect.top + r.height / 2 };
                      };
                      const connectors = Array.isArray(scenario.counterConnectors) ? scenario.counterConnectors : [];
                      const markerId = `cc-arrow-dm-${mapName}-${scenario.id}`;
                      const isDraggingHere = connectorDrag && connectorDrag.scenarioId === scenario.id && connectorDrag.mapName === mapName;
                      let dragLine = null;
                      if (isDraggingHere) {
                        const from = getCenter(connectorDrag.fromPosition);
                        if (from) dragLine = { x1: from.x, y1: from.y, x2: connectorDrag.mouseX - cRect.left, y2: connectorDrag.mouseY - cRect.top };
                      }
                      return (
                        <ConnectorSvg viewBox={`0 0 ${vbW} ${vbH}`} preserveAspectRatio="none">
                          <defs>
                            <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                              <path d="M 0 0 L 10 5 L 0 10 z" fill="#00ff88" />
                            </marker>
                          </defs>
                          {connectors.map((c, i) => {
                            const a = getCenter(c.from);
                            const b = getCenter(c.to);
                            if (!a || !b) return null;
                            return (
                              <path key={`${c.from}-${c.to}-${i}`} className="connector-path" d={`M ${a.x} ${a.y} L ${b.x} ${b.y}`} markerEnd={`url(#${markerId})`}
                                onClick={(e) => { e.stopPropagation(); if (!isReadOnly) removeCounterConnector(mapName, scenario.id, i); }} />
                            );
                          })}
                          {dragLine && <path className="connector-path drag-preview" d={`M ${dragLine.x1} ${dragLine.y1} L ${dragLine.x2} ${dragLine.y2}`} />}
                        </ConnectorSvg>
                      );
                    })()}
                  </div>
                </MobileDrafterCard>
              );
            });
          })}
        </MobileCarouselScroll>
      </MobileDrafterOuter>

      {/* Mobile landscape carousel - 4 boards per page (2×2), General solo */}
      <MobileCarouselOuter $active={mobileLandscapeView === 'board'}>
        <MobileCarouselScroll>
          {(() => {
            const getScenarios = (mapName) => pickMode === 'first'
              ? (mapDrafts[mapName]?.scenariosFirstPick || [])
              : (mapDrafts[mapName]?.scenariosSecondPick || []);

            // All maps → flat sequence paged in groups of 4
            // Flat = concat all of BW's scenarios, then DG, ER, FC, General
            // Inserting a duplicate shifts everything after it down one slot
            const soloMaps = [];

            const flatItems = boardMaps.flatMap(mapName => {
              const slotIndex = boardMaps.indexOf(mapName);
              return getScenarios(mapName).map((scenario, sIdx) => ({ mapName, slotIndex, scenario, sIdx }));
            });

            const renderBoardCard = (mapName, slotIndex, scenario, sIdx, solo = false) => {
              const slotLabel = boardLabels[slotIndex];
              const mapBorderColor = MAP_BORDER_COLORS[slotLabel] || MAP_BORDER_COLORS['General'];
              const borderColorStr = `rgba(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b}, 0.6)`;
              const swapped = scenario.swapSides || false;
              const usBans = swapped ? [2, 3, 5, 26, 21] : [1, 4, 7, 27, 22];
              const enemyBans = swapped ? [1, 4, 7, 27, 22] : [2, 3, 5, 26, 21];
              const usPicks = swapped ? [13, 16, 17, 24, 25] : [14, 15, 18, 23, 20];
              const enemyPicks = swapped ? [14, 15, 18, 23, 20] : [13, 16, 17, 24, 25];
              const usTeam = swapped ? 'Dire' : 'Radiant';
              const enemyTeam = swapped ? 'Radiant' : 'Dire';
              return (
                <MobileBoardCard key={`mobile_${mapName}_${scenario.id}`} $borderColor={borderColorStr} $solo={solo}>
                  <MobileBoardHeader>
                    <MapDropdown
                      value={slotLabel}
                      style={{
                        fontSize: '9px',
                        padding: '2px 18px 2px 6px',
                        color: `rgb(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b})`,
                        borderColor: `rgba(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b}, 0.5)`,
                        background: `rgba(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b}, 0.15)`,
                        backgroundColor: `rgba(${mapBorderColor.r}, ${mapBorderColor.g}, ${mapBorderColor.b}, 0.15)`,
                      }}
                      onChange={(e) => {
                        setBoardLabels(prev => prev.map((l, i) => i === slotIndex ? e.target.value : l));
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {MAP_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </MapDropdown>
                    <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {UserTierService.hasFeatureAccess(userProfile, 'canDuplicate') && (
                        <DuplicateScenarioButton
                          onClick={() => duplicateScenario(pickMode, scenario.id, mapName)}
                          style={{ minWidth: 'unset', padding: '2px 4px', fontSize: '9px', height: 'auto' }}
                        >
                          📋 Dup
                        </DuplicateScenarioButton>
                      )}
                      {UserTierService.hasFeatureAccess(userProfile, 'canSeeCopyToPickMode') && (
                        <CopyScenarioButton
                          onClick={() => handleCopyToOtherMode(scenario, pickMode, mapName)}
                          style={{ minWidth: 'unset', padding: '2px 4px', fontSize: '9px', height: 'auto' }}
                        >
                          📑 Copy→{pickMode === 'first' ? '2nd' : '1st'}
                        </CopyScenarioButton>
                      )}
                      <DeleteScenarioButton
                        onClick={() => deleteScenario(pickMode, scenario.id, mapName)}
                        style={{ minWidth: 'unset', padding: '2px 4px', fontSize: '9px', height: 'auto' }}
                      >
                        🗑
                      </DeleteScenarioButton>
                      <ClearButton
                        onClick={() => updateScenario(pickMode, scenario.id, { picks: Array(24).fill(null) }, mapName)}
                        style={{ padding: '2px 5px', fontSize: '9px', minWidth: 'unset' }}
                      >
                        Clear
                      </ClearButton>
                      <ClearButton
                        onClick={() => handleSwapSides(scenario, mapName)}
                        style={{
                          padding: '2px 5px', fontSize: '11px', minWidth: 'unset',
                          background: swapped ? '#2196F3' : '#333',
                          border: swapped ? '1px solid #2196F3' : '1px solid #555',
                        }}
                      >
                        ⇄
                      </ClearButton>
                    </div>
                  </MobileBoardHeader>
                  <MobileDraftGrid>
                    <MobileSideSection>
                      <MobileSideLabel $team={usTeam}>Us</MobileSideLabel>
                      <MobileHeroRow>
                        {usBans.map(pos => renderPickBox(sIdx, pos, mapName, slotIndex, '43px', '35px'))}
                      </MobileHeroRow>
                      <MobileHeroRow>
                        {usPicks.map(pos => renderPickBox(sIdx, pos, mapName, slotIndex, '43px', '35px'))}
                      </MobileHeroRow>
                    </MobileSideSection>
                    <MobileSideDivider />
                    <MobileSideSection>
                      <MobileSideLabel $team={enemyTeam}>Enemy</MobileSideLabel>
                      <MobileHeroRow>
                        {enemyBans.map(pos => renderPickBox(sIdx, pos, mapName, slotIndex, '43px', '35px'))}
                      </MobileHeroRow>
                      <MobileHeroRow>
                        {enemyPicks.map(pos => renderPickBox(sIdx, pos, mapName, slotIndex, '43px', '35px'))}
                      </MobileHeroRow>
                    </MobileSideSection>
                  </MobileDraftGrid>
                </MobileBoardCard>
              );
            };

            const pages = [];

            // Group flat items into pages of 4
            for (let pageIdx = 0; pageIdx * 4 < flatItems.length; pageIdx++) {
              const pageItems = flatItems.slice(pageIdx * 4, pageIdx * 4 + 4);
              pages.push(
                <MobilePage key={`page_${pageIdx}`}>
                  {pageItems.map(({ mapName, slotIndex, scenario, sIdx }) =>
                    renderBoardCard(mapName, slotIndex, scenario, sIdx, false)
                  )}
                  {pageItems.length < 4 && Array(4 - pageItems.length).fill(null).map((_, i) => (
                    <div key={`empty_${i}`} />
                  ))}
                </MobilePage>
              );
            }

            // Solo full-width pages for General (and any extra maps beyond 4)
            soloMaps.forEach((mapName) => {
              const slotIndex = boardMaps.indexOf(mapName);
              getScenarios(mapName).forEach((scenario, sIdx) => {
                pages.push(renderBoardCard(mapName, slotIndex, scenario, sIdx, true));
              });
            });

            return pages;
          })()}
        </MobileCarouselScroll>
      </MobileCarouselOuter>

      {/* Hero selection popup as centered overlay */}
      {showPopup && selectedBox !== -1 && (
        <>
          {/* Semi-transparent backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 999
            }}
            onClick={() => setShowPopup(false)}
          />

          <HeroSelectPopup ref={popupRef}>
            <div style={{
              padding: '8px 16px',
              borderBottom: '1px solid #444',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontWeight: 'bold', color: '#eee' }}>
                Select Hero for Position {getDisplayPosition(selectedBox)}
              </div>
              <div
                style={{
                  cursor: 'pointer',
                  fontSize: '20px',
                  color: '#888',
                  padding: '0 8px'
                }}
                onClick={() => setShowPopup(false)}
              >
                ×
              </div>
            </div>

            <div style={{ position: 'relative' }}>
              <SearchInput
                ref={searchInputRef}
                type="text"
                placeholder="Search heroes... (Enter: select first, ESC: close)"
                defaultValue=""
                onInput={(e) => setHeroSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                autoFocus={!(window.innerHeight < 600 && window.innerWidth > window.innerHeight)}
              />
              <SearchIcon>🔍</SearchIcon>
            </div>

            <HeroGrid>
              {filteredHeroes.map((hero, index) => (
                <HeroOption
                  key={hero.id}
                  ref={el => heroRefs.current[hero.id] = el}
                  onClick={() => handleHeroSelect(hero)}
                  style={{
                    outline: focusedHeroIndex === index ? '3px solid #667eea' : 'none',
                    outlineOffset: '2px',
                    backgroundColor: focusedHeroIndex === index ? 'rgba(102, 126, 234, 0.2)' : 'transparent'
                  }}
                >
                  <HeroImage hero={hero} size={window.innerHeight < 600 && window.innerWidth > window.innerHeight ? '42px' : '64px'} />
                  <div className="hero-name">{hero.name}</div>
                </HeroOption>
              ))}
              {filteredHeroes.length === 0 && (
                <div style={{ gridColumn: 'span 4', textAlign: 'center', padding: '20px', color: '#888' }}>
                  No heroes match your search
                </div>
              )}
            </HeroGrid>

            {((isForeheadMode && foreheadPicks[selectedBox - 1]?.hero) ||
              (!isForeheadMode && activeScenario && activeScenario.picks && activeScenario.picks[selectedBox - 1]?.hero)) && (
              <PopupControls>
                <RemoveButton onClick={removeHero}>
                  Remove Hero
                </RemoveButton>
              </PopupControls>
            )}
          </HeroSelectPopup>
        </>
      )}

      {/* Drag and drop overlay */}
      <DragOverlay $show={isDragOver}>
        <DragOverlayContent>
          <DragOverlayIcon>📄</DragOverlayIcon>
          <div>Drop to import draft</div>
        </DragOverlayContent>
      </DragOverlay>

      {/* Confirmation dialog for unsaved changes */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        title="Unsaved Changes"
        message="You have unsaved changes. Would you like to save them before continuing?"
        onSave={handleConfirmSave}
        onDiscard={handleConfirmDiscard}
        onCancel={handleConfirmCancel}
        saveButtonText="Save & Continue"
        discardButtonText="Discard Changes"
        cancelButtonText="Cancel"
      />

      {/* Selection box for multi-select */}
      {isSelecting && (
        <SelectionBox
          style={{
            left: `${Math.min(selectionStart.x, selectionEnd.x)}px`,
            top: `${Math.min(selectionStart.y, selectionEnd.y)}px`,
            width: `${Math.abs(selectionEnd.x - selectionStart.x)}px`,
            height: `${Math.abs(selectionEnd.y - selectionStart.y)}px`,
          }}
        />
      )}

      {/* Canvas ripple effect on drop */}
      <CanvasRipple
        $show={ripplePosition.show}
        $x={ripplePosition.x}
        $y={ripplePosition.y}
      />

      {/* Show Both Overlay */}
      {showBothOverlay && (
        <ScenarioSelectionOverlay onClick={() => setShowBothOverlay(false)} onWheel={(e) => e.stopPropagation()}>
          <ScenarioSelectionCloseButton onClick={() => setShowBothOverlay(false)}>✕</ScenarioSelectionCloseButton>
          <ScenarioSelectionHeader onClick={(e) => e.stopPropagation()}>
            <ScenarioSelectionTitle style={{ color: '#ce93d8' }}>All Scenarios — Both Pick Modes</ScenarioSelectionTitle>
            <ScenarioSelectionSubtitle>Press Esc or click outside to close</ScenarioSelectionSubtitle>
          </ScenarioSelectionHeader>

          <div onClick={(e) => e.stopPropagation()} style={{ padding: '0 12px 12px' }}>
            {boardMaps.map((mapName, slotIndex) => {
              const slotLabel = boardLabels[slotIndex];
              const mapColor = MAP_BORDER_COLORS[slotLabel] || MAP_BORDER_COLORS['General'];
              const mapData = mapDrafts[mapName] || {};
              const firstPickScenarios = mapData.scenariosFirstPick || [];
              const secondPickScenarios = mapData.scenariosSecondPick || [];

              const renderScenarioBoard = (scenario, idx, mode) => {
                const swapped = scenario.swapSides || false;
                const usBans    = swapped ? [2, 3, 5, 26, 21]    : [1, 4, 7, 27, 22];
                const enemyBans = swapped ? [1, 4, 7, 27, 22]    : [2, 3, 5, 26, 21];
                const usPicks   = swapped ? [13, 16, 17, 24, 25] : [14, 15, 18, 23, 20];
                const enemyPicks= swapped ? [14, 15, 18, 23, 20] : [13, 16, 17, 24, 25];
                const color = mode === 'first' ? '#1de9b6' : '#61dafb';
                const modeLabel = mode === 'first' ? '1st Pick' : '2nd Pick';
                const renderBox = (position, team) => {
                  const pick = scenario.picks?.[position - 1];
                  const isBanPosition = banPositions.includes(position);
                  return (
                    <PickBox key={position} $position={position} $team={team} style={{ pointerEvents: 'none' }}>
                      <PositionLabel $position={position} $compact={false}>{getDisplayPosition(position)}</PositionLabel>
                      {pick?.hero ? <HeroContainer><HeroImage hero={pick.hero} $grayscale={false} /></HeroContainer> : null}
                      {isBanPosition && <BanCross>✕</BanCross>}
                    </PickBox>
                  );
                };
                return (
                  <DraftBoardContainer
                    key={scenario.id}
                    $swapSides={swapped}
                    style={{ cursor: 'default', flexShrink: 0, border: `3px solid ${color}`, ...(desktopView === 'drafter' && { width: '50vw', minWidth: '50vw', maxWidth: '50vw' }) }}
                    $fromMatchPull={scenario.fromMatchPull}
                    $matchColorIndex={scenario.matchColorIndex}
                  >
                    <ScenarioInput type="text" value={scenario.title || ''} readOnly style={{ pointerEvents: 'none' }} />
                    <ScenarioTitle>
                      <span style={{ color }}>{modeLabel}</span> Scenario {idx + 1}
                    </ScenarioTitle>
                    {desktopView === 'drafter' ? (
                      <>
                        <MobileBansSection style={{ marginTop: '8px' }}>
                          <MobileBansGroup>{usBans.map(pos => renderBox(pos, swapped ? 'Dire' : 'Radiant'))}</MobileBansGroup>
                          <MobileBansGroup>{enemyBans.map(pos => renderBox(pos, swapped ? 'Radiant' : 'Dire'))}</MobileBansGroup>
                        </MobileBansSection>
                        <MobileTeamsLabel>
                          <TeamHeader $team={swapped ? 'Dire' : 'Radiant'} style={{ fontSize: '15px', width: 'auto', padding: '0 30px' }}>Us</TeamHeader>
                          <TeamHeader $team={swapped ? 'Radiant' : 'Dire'} style={{ fontSize: '15px', width: 'auto', padding: '0 30px' }}>Enemy</TeamHeader>
                        </MobileTeamsLabel>
                        <MobilePicksSection style={{ gap: '20px' }}>
                          <MobilePickColumn style={{ gap: '8px' }}>{usPicks.map(pos => renderBox(pos, swapped ? 'Dire' : 'Radiant'))}</MobilePickColumn>
                          <MobilePickColumn style={{ gap: '8px' }}>{enemyPicks.map(pos => renderBox(pos, swapped ? 'Radiant' : 'Dire'))}</MobilePickColumn>
                        </MobilePicksSection>
                      </>
                    ) : (
                      <>
                        <TeamsContainer>
                          <TeamHeader $team="Radiant">Radiant</TeamHeader>
                          <TeamHeader $team="Dire">Dire</TeamHeader>
                        </TeamsContainer>
                        <PicksContainer>
                          <TeamColumn>{[1, 4, 7, 14, 15, 18, 27, 22, 23, 20].map(pos => renderBox(pos, 'Radiant'))}</TeamColumn>
                          <TeamColumn>{[2, 3, 5, 13, 16, 17, 26, 21, 24, 25].map(pos => renderBox(pos, 'Dire'))}</TeamColumn>
                        </PicksContainer>
                      </>
                    )}
                  </DraftBoardContainer>
                );
              };

              return (
                <MapSectionWrapper key={`${mapName}_${slotIndex}`} $mapColor={mapColor} style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 4px 2px' }}>
                    <div style={{
                      background: `rgba(${mapColor.r}, ${mapColor.g}, ${mapColor.b}, 0.15)`,
                      color: `rgb(${mapColor.r}, ${mapColor.g}, ${mapColor.b})`,
                      border: `1px solid rgba(${mapColor.r}, ${mapColor.g}, ${mapColor.b}, 0.5)`,
                      borderRadius: '999px', padding: '3px 14px', fontSize: '11px',
                      fontWeight: 'bold', letterSpacing: '0.5px', textTransform: 'uppercase',
                    }}>{slotLabel}</div>
                  </div>

                  {/* Side-by-side columns: 1st Pick left, 2nd Pick right */}
                  <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', padding: '4px 4px 8px' }}>
                    {[['first', firstPickScenarios, '#1de9b6', '1st Pick'], ['second', secondPickScenarios, '#61dafb', '2nd Pick']].map(([mode, scenarios, color, label]) => (
                      <div key={mode} style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ textAlign: 'center', marginBottom: 8 }}>
                          <span style={{ color, fontSize: 13, fontWeight: 'bold', borderBottom: `2px solid ${color}`, paddingBottom: 2 }}>{label}</span>
                          <span style={{ color: '#666', fontSize: 12, marginLeft: 8 }}>{scenarios.length} scenario{scenarios.length !== 1 ? 's' : ''}</span>
                        </div>
                        {scenarios.length === 0 ? (
                          <div style={{ textAlign: 'center', color: '#444', fontSize: 12 }}>No scenarios</div>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 12, overflowX: 'auto', paddingBottom: 8 }}>
                            {scenarios.map((scenario, idx) => renderScenarioBoard(scenario, idx, mode))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </MapSectionWrapper>
              );
            })}
          </div>
        </ScenarioSelectionOverlay>
      )}

      {/* Scenario Selection Overlay for Copy From */}
      {showScenarioSelection && (
        <ScenarioSelectionOverlay onClick={handleCancelScenarioSelection}>
          <ScenarioSelectionCloseButton onClick={handleCancelScenarioSelection}>
            ✕
          </ScenarioSelectionCloseButton>
          <ScenarioSelectionHeader onClick={(e) => e.stopPropagation()}>
            <ScenarioSelectionTitle>
              Select Scenario to Copy From
            </ScenarioSelectionTitle>
            <ScenarioSelectionSubtitle>
              Click any {pickMode === 'first' ? '2nd Pick' : '1st Pick'} scenario across any map to copy it here
            </ScenarioSelectionSubtitle>
          </ScenarioSelectionHeader>
          <AllMapsContainer onClick={(e) => e.stopPropagation()} style={{ flex: 1, alignItems: 'flex-start', padding: '0 12px 12px' }}>
            {boardMaps.map((mapName, slotIndex) => {
              const sourcePickMode = pickMode === 'first' ? 'second' : 'first';
              const mapData = mapDrafts[mapName] || {};
              const sourceScenarios = sourcePickMode === 'first'
                ? (mapData.scenariosFirstPick || [])
                : (mapData.scenariosSecondPick || []);
              const slotLabel = boardLabels[slotIndex];
              const mapColor = MAP_BORDER_COLORS[slotLabel] || MAP_BORDER_COLORS['General'];

              return (
                <MapSectionWrapper key={`${mapName}_${slotIndex}`} $mapColor={mapColor}>
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 4px 2px' }}>
                    <div style={{
                      background: `rgba(${mapColor.r}, ${mapColor.g}, ${mapColor.b}, 0.15)`,
                      color: `rgb(${mapColor.r}, ${mapColor.g}, ${mapColor.b})`,
                      border: `1px solid rgba(${mapColor.r}, ${mapColor.g}, ${mapColor.b}, 0.5)`,
                      borderRadius: '999px',
                      padding: '3px 14px',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}>
                      {slotLabel}
                    </div>
                  </div>
                  <ScenarioBoardsRow style={{ padding: '4px 4px 8px' }}>
                    {sourceScenarios.map((sourceScenario, idx) => {
                      const swapped = sourceScenario.swapSides || false;
                      const usBans    = swapped ? [2, 3, 5, 26, 21]    : [1, 4, 7, 27, 22];
                      const enemyBans = swapped ? [1, 4, 7, 27, 22]    : [2, 3, 5, 26, 21];
                      const usPicks   = swapped ? [13, 16, 17, 24, 25] : [14, 15, 18, 23, 20];
                      const enemyPicks= swapped ? [14, 15, 18, 23, 20] : [13, 16, 17, 24, 25];
                      const renderBox = (position, team) => {
                        const pick = sourceScenario.picks?.[position - 1];
                        const isBanPosition = banPositions.includes(position);
                        return (
                          <PickBox key={position} $position={position} $team={team} style={{ pointerEvents: 'none' }}>
                            <PositionLabel $position={position} $compact={false}>{getDisplayPosition(position)}</PositionLabel>
                            {pick?.hero ? <HeroContainer><HeroImage hero={pick.hero} $grayscale={false} /></HeroContainer> : null}
                            {isBanPosition && <BanCross>✕</BanCross>}
                          </PickBox>
                        );
                      };
                      return (
                        <DraftBoardContainer
                          key={sourceScenario.id}
                          $swapSides={swapped}
                          style={{ cursor: 'pointer', ...(desktopView === 'drafter' && { width: '50vw', minWidth: '50vw', maxWidth: '50vw' }) }}
                          onClick={() => handleSelectSourceScenario(sourceScenario, sourcePickMode, mapName)}
                          $fromMatchPull={sourceScenario.fromMatchPull}
                          $matchColorIndex={sourceScenario.matchColorIndex}
                        >
                          <ScenarioInput type="text" value={sourceScenario.title || ''} readOnly style={{ pointerEvents: 'none' }} />
                          <ScenarioTitle>
                            {sourcePickMode === 'second'
                              ? <span style={{ color: '#61dafb' }}>2nd Pick</span>
                              : <span style={{ color: '#1de9b6' }}>1st Pick</span>} Scenario {idx + 1}
                          </ScenarioTitle>
                          {desktopView === 'drafter' ? (
                            <>
                              <MobileBansSection style={{ marginTop: '8px' }}>
                                <MobileBansGroup>{usBans.map(pos => renderBox(pos, swapped ? 'Dire' : 'Radiant'))}</MobileBansGroup>
                                <MobileBansGroup>{enemyBans.map(pos => renderBox(pos, swapped ? 'Radiant' : 'Dire'))}</MobileBansGroup>
                              </MobileBansSection>
                              <MobileTeamsLabel>
                                <TeamHeader $team={swapped ? 'Dire' : 'Radiant'} style={{ fontSize: '15px', width: 'auto', padding: '0 30px' }}>Us</TeamHeader>
                                <TeamHeader $team={swapped ? 'Radiant' : 'Dire'} style={{ fontSize: '15px', width: 'auto', padding: '0 30px' }}>Enemy</TeamHeader>
                              </MobileTeamsLabel>
                              <MobilePicksSection style={{ gap: '20px' }}>
                                <MobilePickColumn style={{ gap: '8px' }}>{usPicks.map(pos => renderBox(pos, swapped ? 'Dire' : 'Radiant'))}</MobilePickColumn>
                                <MobilePickColumn style={{ gap: '8px' }}>{enemyPicks.map(pos => renderBox(pos, swapped ? 'Radiant' : 'Dire'))}</MobilePickColumn>
                              </MobilePicksSection>
                            </>
                          ) : (
                            <>
                              <TeamsContainer>
                                <TeamHeader $team="Radiant">Radiant</TeamHeader>
                                <TeamHeader $team="Dire">Dire</TeamHeader>
                              </TeamsContainer>
                              <PicksContainer>
                                <TeamColumn>{[1, 4, 7, 14, 15, 18, 27, 22, 23, 20].map(pos => renderBox(pos, 'Radiant'))}</TeamColumn>
                                <TeamColumn>{[2, 3, 5, 13, 16, 17, 26, 21, 24, 25].map(pos => renderBox(pos, 'Dire'))}</TeamColumn>
                              </PicksContainer>
                            </>
                          )}
                        </DraftBoardContainer>
                      );
                    })}
                  </ScenarioBoardsRow>
                </MapSectionWrapper>
              );
            })}
          </AllMapsContainer>
        </ScenarioSelectionOverlay>
      )}
    </MainContainer>
  );
};

// Simple Button component
const Button = styled.button`
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  }
`;

export default DraftBoard;
